<template>
  <div class="manual-page">
    <CCard class="manual-card">
      <CCardHeader class="manual-header user-page-banner">
        <div class="d-flex align-items-center justify-content-between w-100 flex-wrap">
          <h4 class="card-title mb-0 text-white">
            <CIcon name="cil-book" size="xl" class="mr-2"/> {{ isThai ? 'คู่มือการใช้งานและขั้นตอนการดำเนินงาน' : 'User Manual & Workflow' }}
          </h4>
          <span class="badge badge-light px-3 py-2 text-primary font-weight-bold shadow-sm mt-2 mt-md-0">
            {{ isThai ? 'สำหรับผู้แจ้งซ่อม (User Role)' : 'For Requester (User Role)' }}
          </span>
        </div>
      </CCardHeader>

      <CCardBody class="manual-body p-4" :class="{ 'manual-user-only': userRole === 'user' }">
        
        <CTabs variant="pills" :active-tab="0" :fade="false">
          <!-- 1. USER TAB -->
          <CTab :title="$t('userManual.tabUser')" v-if="userRole === 'admin' || userRole === 'user'">
            <div class="manual-user-container mt-3">
              
              <!-- Section A: 4-Step High-level Workflow -->
              <div class="workflow-summary-section mb-5">
                <div class="d-flex align-items-center mb-3">
                  <div class="section-badge mr-2">Overview</div>
                  <h5 class="manual-section-title mb-0">
                    {{ isThai ? 'ขั้นตอนการทำงานภาพรวม (Workflow Overview)' : 'Workflow for requesters (user)' }}
                  </h5>
                </div>
                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'วงจรการดำเนินงานแจ้งซ่อมตั้งแต่ส่งคำขอ ตรวจสอบ มอบหมาย จนถึงการซ่อมเสร็จสิ้นและประเมินผล' 
                    : 'End-to-end maintenance request cycle from submission, triage, onsite repair, to final rating.' }}
                </p>

                <div class="flowchart-container">
                  <div class="flow-step">
                    <div class="flow-step-num">01</div>
                    <div class="flow-icon bg-primary"><CIcon name="cil-pencil" size="lg" class="text-white"/></div>
                    <div class="flow-title">{{ isThai ? '1. ส่งคำขอแจ้งซ่อม' : '1. Submit Request' }}</div>
                    <div class="flow-desc">{{ isThai ? 'เลือกประเภทงาน ระบุสถานที่ และปัญหาพร้อมรูปถ่าย' : 'Describe the issue, location, and work type' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  
                  <div class="flow-step">
                    <div class="flow-step-num">02</div>
                    <div class="flow-icon bg-info"><CIcon name="cil-clock" size="lg" class="text-white"/></div>
                    <div class="flow-title">{{ isThai ? '2. รอรับเรื่องและมอบหมาย' : '2. Awaiting Triage' }}</div>
                    <div class="flow-desc">{{ isThai ? 'หัวหน้างานตรวจสอบใบงานและมอบหมายช่างผู้รับผิดชอบ' : 'Supervisor reviews and assigns technician' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  
                  <div class="flow-step">
                    <div class="flow-step-num">03</div>
                    <div class="flow-icon bg-warning text-dark"><CIcon name="cil-settings" size="lg" class="text-white"/></div>
                    <div class="flow-title">{{ isThai ? '3. ช่างเข้าดำเนินการ' : '3. Technician Works' }}</div>
                    <div class="flow-desc">{{ isThai ? 'ช่างลงพื้นที่ปฏิบัติงานซ่อมตามวันเวลาที่กำหนด' : 'Assigned technician performs the repair on site' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  
                  <div class="flow-step">
                    <div class="flow-step-num">04</div>
                    <div class="flow-icon bg-success"><CIcon name="cil-star" size="lg" class="text-white"/></div>
                    <div class="flow-title">{{ isThai ? '4. ตรวจรับและประเมินผล' : '4. Confirm & Rate' }}</div>
                    <div class="flow-desc">{{ isThai ? 'เมื่อซ่อมเสร็จ ผู้แจ้งตรวจรับงานและให้คะแนนความพึงพอใจ' : 'Confirm work done and rate satisfaction score' }}</div>
                  </div>
                </div>
              </div>

              <!-- Section B: Step-by-Step Screenshot Visual Guide -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge mr-2">Guide</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'คู่มือวิธีการแจ้งซ่อมทีละขั้นตอน (Step-by-Step Reporting Guide)' : 'Step-by-Step Repair Reporting Guide' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [1,2,3,4,5]" 
                      :key="'chip-user-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeStep === s }"
                      @click="highlightStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>
                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'คำแนะนำการกรอกแบบฟอร์มแจ้งซ่อมอย่างละเอียด พร้อมภาพจำลองหน้าจอเพื่อความสะดวก รวดเร็ว และถูกต้อง' 
                    : 'Detailed instructions on how to fill in the repair form with visual interface mockups.' }}
                </p>

                <div class="step-cards-list">

                  <!-- Step 1: เลือกประเภทงานซ่อม -->
                  <div id="step-card-user-1" class="guide-card" :class="{ 'step-jump-pulse': activeStep === 1 }">
                    <div class="guide-card-header">
                      <div class="guide-step-pill">1</div>
                      <div class="guide-header-text">
                        <h6 class="guide-step-title mb-1">
                          {{ isThai ? 'เลือกประเภทงานซ่อม (Select Job Type)' : 'Select Repair Category (Job Type)' }}
                        </h6>
                        <span class="guide-step-subtitle">
                          {{ isThai ? 'เลือกหมวดหมู่งานที่ต้องการแจ้งซ่อมผ่านไอคอน หรือพิมพ์ค้นหา' : 'Choose a category using quick icon buttons or search by keyword' }}
                        </span>
                      </div>
                    </div>

                    <div class="guide-card-body">
                      <!-- Visual Mockup: Category Grid -->
                      <div class="ui-mockup-frame">
                        <div class="ui-mockup-header">
                          <span class="ui-mockup-dot red"></span>
                          <span class="ui-mockup-dot yellow"></span>
                          <span class="ui-mockup-dot green"></span>
                          <span class="ui-mockup-title">{{ isThai ? 'จำลองหน้าจอ: ส่วนเลือกประเภทงานซ่อม' : 'UI Preview: Job Type Selection' }}</span>
                        </div>
                        <div class="ui-mockup-content">
                          <div class="mock-label">{{ isThai ? 'ประเภทงานซ่อม *' : 'Job Type *' }}</div>
                          <div class="mock-category-box">
                            <div class="mock-section-head">
                              <span><CIcon name="cil-star" class="text-warning mr-1"/> {{ isThai ? 'ใช้งานบ่อย' : 'Frequently Used' }}</span>
                            </div>
                            <div class="mock-icon-row">
                              <div class="mock-icon-tile active">
                                <CIcon name="cil-snowflake" size="lg"/>
                                <span class="mock-tile-tooltip">{{ isThai ? 'เครื่องปรับอากาศ' : 'Air Conditioner' }}</span>
                              </div>
                              <div class="mock-icon-tile">
                                <CIcon name="cil-lightbulb" size="lg"/>
                              </div>
                              <div class="mock-icon-tile">
                                <CIcon name="cil-bolt" size="lg"/>
                              </div>
                              <div class="mock-icon-tile">
                                <CIcon name="cil-wifi-signal-1" size="lg"/>
                              </div>
                              <div class="mock-icon-tile">
                                <CIcon name="cil-monitor" size="lg"/>
                              </div>
                              <div class="mock-icon-tile">
                                <CIcon name="cil-print" size="lg"/>
                              </div>
                              <div class="mock-icon-tile">
                                <CIcon name="cil-tv" size="lg"/>
                              </div>
                            </div>
                            <!-- Selected Bar -->
                            <div class="mock-selected-bar mt-3">
                              <span class="text-primary font-weight-bold">{{ isThai ? 'ประเภทงานซ่อม:' : 'Job Type:' }}</span>
                              <span class="ml-2 font-weight-bold text-dark">{{ isThai ? 'เครื่องปรับอากาศและระบบเครื่องกล' : 'Air Conditioning & Mechanical Systems' }}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="guide-instructions mt-3">
                        <ul class="instruction-list">
                          <li><strong>{{ isThai ? 'คลิกไอคอนที่ต้องการ:' : 'Click the matching icon:' }}</strong> {{ isThai ? 'ระบบได้รวมหมวดหมู่ที่ใช้งานบ่อยไว้ในแถวแรก เช่น แอร์, ไฟฟ้า, ประปา, คอมพิวเตอร์, เคเบิลทีวี' : 'Frequently used categories are pinned in the first row for one-click access.' }}</li>
                          <li><strong>{{ isThai ? 'ใช้ช่องค้นหา:' : 'Use keyword search:' }}</strong> {{ isThai ? 'สามารถพิมพ์คำค้น เช่น "แอร์", "wifi", "ทีวี" ในช่องค้นหาด้านล่างเพื่อกรองประเภทงานทันที' : 'Type keywords like "air", "wifi", "tv" to instantly filter categories.' }}</li>
                          <li><strong>{{ isThai ? 'ตรวจเช็คแถบสีฟ้าด้านล่าง:' : 'Check feedback banner:' }}</strong> {{ isThai ? 'เมื่อคลิกเลือกแล้ว แถบสีฟ้าด้านล่างจะแสดงชื่อประเภทงานซ่อมที่ท่านได้เลือกไว้' : 'The blue status bar confirms your selected repair category.' }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Step 2: อธิบายปัญหาและแนบรูปภาพ -->
                  <div id="step-card-user-2" class="guide-card" :class="{ 'step-jump-pulse': activeStep === 2 }">
                    <div class="guide-card-header">
                      <div class="guide-step-pill">2</div>
                      <div class="guide-header-text">
                        <h6 class="guide-step-title mb-1">
                          {{ isThai ? 'ระบุปัญหาการซ่อมและแนบรูปถ่าย (Describe Issue & Attach Photos)' : 'Describe Issue & Attach Photos' }}
                        </h6>
                        <span class="guide-step-subtitle">
                          {{ isThai ? 'อธิบายอาการชำรุดให้ชัดเจน และถ่ายภาพจุดที่เกิดปัญหา' : 'Provide clear defect details and upload photos of the damaged area' }}
                        </span>
                      </div>
                    </div>

                    <div class="guide-card-body">
                      <!-- Visual Mockup: Problem & Upload -->
                      <div class="ui-mockup-frame">
                        <div class="ui-mockup-header">
                          <span class="ui-mockup-dot red"></span>
                          <span class="ui-mockup-dot yellow"></span>
                          <span class="ui-mockup-dot green"></span>
                          <span class="ui-mockup-title">{{ isThai ? 'จำลองหน้าจอ: รายละเอียดปัญหาและแนบรูปภาพ' : 'UI Preview: Problem & Photo Upload' }}</span>
                        </div>
                        <div class="ui-mockup-content">
                          <div class="mock-label">{{ isThai ? 'ปัญหา/งานซ่อม *' : 'Repair Issue / Description *' }}</div>
                          <div class="mock-textarea">
                            {{ isThai ? 'เครื่องปรับอากาศมีน้ำหยดลงบนโต๊ะทำงาน และพัดลมแอร์มีเสียงดังผิดปกติ' : 'Air conditioner has a water leak dripping onto the desk and the blower makes a loud noise.' }}
                          </div>
                          
                          <div class="mock-label mt-3">{{ isThai ? 'แนบรูปภาพ *' : 'Attach Photo *' }}</div>
                          <div class="mock-upload-box">
                            <div class="mock-upload-inner">
                              <CIcon name="cil-camera" size="xl" class="text-primary mb-1"/>
                              <div class="text-primary font-weight-bold small">{{ isThai ? 'ถ่ายรูป/เพิ่มรูป' : 'Take photo / Add image' }}</div>
                              <small class="text-muted">{{ isThai ? 'รองรับไฟล์รูปภาพ เช่น jpg, jpeg, png' : 'Supports jpg, jpeg, png files' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="guide-instructions mt-3">
                        <ul class="instruction-list">
                          <li><strong>{{ isThai ? 'ระบุอาการเสีย:' : 'Be descriptive:' }}</strong> {{ isThai ? 'อธิบายอาการเสียและบริเวณที่พบ เช่น "เปิดไม่ติด", "น้ำรั่วหยด", "ไฟกระพริบ"' : 'Explain visible symptoms like leaks, unusual noises, or power failure.' }}</li>
                          <li><strong>{{ isThai ? 'แนบภาพความเสียหาย:' : 'Attach clear photos:' }}</strong> {{ isThai ? 'การแนบภาพช่วยให้ช่างประเมินและเตรียมอะไหล่ได้ตรงจุด ช่วยให้งานซ่อมเสร็จรวดเร็วขึ้น' : 'Photos allow technicians to prepare proper spare parts and tools before arriving on site.' }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Step 3: ระบุสถานที่ปฏิบัติงาน -->
                  <div id="step-card-user-3" class="guide-card" :class="{ 'step-jump-pulse': activeStep === 3 }">
                    <div class="guide-card-header">
                      <div class="guide-step-pill">3</div>
                      <div class="guide-header-text">
                        <h6 class="guide-step-title mb-1">
                          {{ isThai ? 'ระบุสถานที่ปฏิบัติงาน (Specify Location & Room)' : 'Specify Location & Room Details' }}
                        </h6>
                        <span class="guide-step-subtitle">
                          {{ isThai ? 'เลือกว่าอยู่ในอาคารหรือนอกอาคาร และค้นหาชื่ออาคาร' : 'Select whether it is indoor or outdoor and search building name' }}
                        </span>
                      </div>
                    </div>

                    <div class="guide-card-body">
                      <!-- Visual Mockup: Location -->
                      <div class="ui-mockup-frame">
                        <div class="ui-mockup-header">
                          <span class="ui-mockup-dot red"></span>
                          <span class="ui-mockup-dot yellow"></span>
                          <span class="ui-mockup-dot green"></span>
                          <span class="ui-mockup-title">{{ isThai ? 'จำลองหน้าจอ: ระบุสถานที่' : 'UI Preview: Location & Building' }}</span>
                        </div>
                        <div class="ui-mockup-content">
                          <div class="mock-label">{{ isThai ? 'พื้นที่ปฏิบัติงาน *' : 'Work Area *' }}</div>
                          <div class="row no-gutters mb-3">
                            <div class="col-6 pr-1">
                              <div class="mock-location-toggle active">
                                <CIcon name="cil-building" size="lg" class="text-primary mr-2"/>
                                <span class="font-weight-bold">{{ isThai ? 'ในอาคาร (Building)' : 'Building' }}</span>
                              </div>
                            </div>
                            <div class="col-6 pl-1">
                              <div class="mock-location-toggle">
                                <CIcon name="cil-location-pin" size="lg" class="text-muted mr-2"/>
                                <span>{{ isThai ? 'นอกอาคาร (Outdoor)' : 'Outdoor' }}</span>
                              </div>
                            </div>
                          </div>

                          <div class="mock-label">{{ isThai ? 'อาคาร/ห้อง *' : 'Building / Room *' }}</div>
                          <div class="mock-input-with-icon">
                            <CIcon name="cil-search" class="mock-input-icon"/>
                            <div class="mock-input-text font-weight-bold text-dark">
                              {{ isThai ? 'อาคารวนาเวศน์ - ห้อง 302' : 'Wanawes Building - Room 302' }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="guide-instructions mt-3">
                        <ul class="instruction-list">
                          <li><strong>{{ isThai ? 'เลือกลักษณะพื้นที่:' : 'Location Type:' }}</strong> {{ isThai ? 'คลิก "ในอาคาร" หรือ "นอกอาคาร" ตามตำแหน่งที่ต้องการให้ช่างเข้าซ่อม' : 'Choose Building or Outdoor depending on the repair area.' }}</li>
                          <li><strong>{{ isThai ? 'ค้นหาชื่ออาคาร:' : 'Autocomplete Building Search:' }}</strong> {{ isThai ? 'พิมพ์ชื่ออาคารภาษาไทยหรือภาษาอังกฤษ เช่น "วนาเวศน์", "สักทอง", "canteen" ระบบจะมีรายการแนะนำขึ้นมาให้คลิกเลือก' : 'Type building name in Thai or English to see auto-suggestions.' }}</li>
                          <li><strong>{{ isThai ? 'ระบุหมายเลขห้อง:' : 'Room Number:' }}</strong> {{ isThai ? 'พิมพ์เครื่องหมายขีด (-) ตามด้วยเลขห้อง เช่น "C1-205" หรือ "สักทอง 1-305"' : 'Specify room number with a hyphen, e.g., "C1-205" or "Saktong 1-305".' }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Step 4: ข้อมูลผู้แจ้งและยืนยันการแจ้งซ่อม -->
                  <div id="step-card-user-4" class="guide-card" :class="{ 'step-jump-pulse': activeStep === 4 }">
                    <div class="guide-card-header">
                      <div class="guide-step-pill">4</div>
                      <div class="guide-header-text">
                        <h6 class="guide-step-title mb-1">
                          {{ isThai ? 'ตรวจสอบข้อมูลผู้แจ้งและยืนยัน (Submit & Confirm)' : 'Verify Contact Info & Submit' }}
                        </h6>
                        <span class="guide-step-subtitle">
                          {{ isThai ? 'ตรวจเช็คเบอร์โทรติดต่อกลับ แล้วกดปุ่มยืนยันแจ้งซ่อม' : 'Verify phone number for on-site contact and submit ticket' }}
                        </span>
                      </div>
                    </div>

                    <div class="guide-card-body">
                      <!-- Visual Mockup: User info & submit -->
                      <div class="ui-mockup-frame">
                        <div class="ui-mockup-header">
                          <span class="ui-mockup-dot red"></span>
                          <span class="ui-mockup-dot yellow"></span>
                          <span class="ui-mockup-dot green"></span>
                          <span class="ui-mockup-title">{{ isThai ? 'จำลองหน้าจอ: ข้อมูลผู้แจ้งและปุ่มยืนยัน' : 'UI Preview: Requester Information & Submit' }}</span>
                        </div>
                        <div class="ui-mockup-content">
                          <div class="row">
                            <div class="col-md-6 mb-2">
                              <small class="text-muted">{{ isThai ? 'ชื่อผู้แจ้ง' : 'Requester Name' }}</small>
                              <div class="font-weight-bold text-dark">ณิชา พรหมรักษา (นักศึกษา/บุคลากร)</div>
                            </div>
                            <div class="col-md-6 mb-2">
                              <small class="text-muted">{{ isThai ? 'สังกัด/สำนักวิชา' : 'Affiliation / School' }}</small>
                              <div class="font-weight-bold text-dark">สำนักวิชาเทคโนโลยีสารสนเทศ</div>
                            </div>
                            <div class="col-md-12 mt-2">
                              <small class="text-muted">{{ isThai ? 'เบอร์โทรศัพท์ติดต่อ *' : 'Phone Number *' }}</small>
                              <div class="mock-input-box font-weight-bold text-primary">089-123-4567</div>
                            </div>
                          </div>

                          <div class="d-flex justify-content-end mt-3 pt-3 border-top">
                            <button type="button" class="btn btn-secondary btn-sm mr-2 px-3" disabled>{{ isThai ? 'ยกเลิก' : 'Cancel' }}</button>
                            <button type="button" class="btn btn-primary btn-sm px-4 shadow font-weight-bold" disabled>
                              {{ isThai ? 'ยืนยันการแจ้งซ่อม' : 'Submit Request' }}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="guide-instructions mt-3">
                        <ul class="instruction-list">
                          <li><strong>{{ isThai ? 'เบอร์โทรศัพท์ที่ติดต่อได้:' : 'Valid Contact Phone:' }}</strong> {{ isThai ? 'กรุณากรอกเบอร์มือถือที่ติดต่อได้สะดวก เพื่อให้ช่างสามารถโทรสอบถามตำแหน่งหรือประสานงานก่อนเข้าพื้นที่' : 'Ensure your phone number is correct so the technician can reach you.' }}</li>
                          <li><strong>{{ isThai ? 'รับรหัสใบแจ้งซ่อม:' : 'Receive Ticket Number:' }}</strong> {{ isThai ? 'เมื่อกดยืนยัน ระบบจะแสดง Pop-up แจ้งเตือนรหัสใบแจ้งซ่อม เช่น "FIX003058" เพื่อใช้ในการอ้างอิงและติดตามงาน' : 'Upon submission, a confirmation pop-up will display your Ticket ID (e.g. FIX003058).' }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Step 5: การติดตามสถานะและประเมินความพึงพอใจ -->
                  <div id="step-card-user-5" class="guide-card" :class="{ 'step-jump-pulse': activeStep === 5 }">
                    <div class="guide-card-header">
                      <div class="guide-step-pill">5</div>
                      <div class="guide-header-text">
                        <h6 class="guide-step-title mb-1">
                          {{ isThai ? 'ติดตามสถานะและประเมินความพึงพอใจ (Tracking & Rating)' : 'Track Progress & Rate Satisfaction' }}
                        </h6>
                        <span class="guide-step-subtitle">
                          {{ isThai ? 'ตรวจสอบความคืบหน้าที่เมนู "ประวัติ (History)" และให้คะแนนเมื่อซ่อมเสร็จ' : 'Monitor progress in History menu and rate service once completed' }}
                        </span>
                      </div>
                    </div>

                    <div class="guide-card-body">
                      <!-- Visual Mockup: History & Rating -->
                      <div class="ui-mockup-frame">
                        <div class="ui-mockup-header">
                          <span class="ui-mockup-dot red"></span>
                          <span class="ui-mockup-dot yellow"></span>
                          <span class="ui-mockup-dot green"></span>
                          <span class="ui-mockup-title">{{ isThai ? 'จำลองหน้าจอ: ประวัติและสถานะใบงาน' : 'UI Preview: Ticket Status & Satisfaction Rating' }}</span>
                        </div>
                        <div class="ui-mockup-content">
                          <div class="mock-ticket-item">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                              <span class="font-weight-bold text-primary">#FIX003058</span>
                              <span class="badge badge-success px-2 py-1 font-weight-bold">{{ isThai ? 'ซ่อมเสร็จสิ้น (Completed)' : 'Completed' }}</span>
                            </div>
                            <div class="small text-dark font-weight-bold">เครื่องปรับอากาศมีน้ำหยด — วนาเวศน์ ห้อง 302</div>
                            <div class="small text-muted mt-1">{{ isThai ? 'ช่างผู้รับผิดชอบ: โชติวัต กิจรักษ์' : 'Technician: Chotiwat Kitrak' }}</div>
                            
                            <!-- Rating Action -->
                            <div class="mock-rating-bar mt-3 pt-2 border-top d-flex align-items-center justify-content-between">
                              <span class="small font-weight-bold text-dark">{{ isThai ? 'ประเมินความพึงพอใจ:' : 'Rate satisfaction:' }}</span>
                              <div class="mock-stars">
                                <span class="text-warning font-weight-bold">★ ★ ★ ★ ★</span>
                                <span class="badge badge-primary ml-2">{{ isThai ? 'ให้คะแนน' : 'Rate' }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="guide-instructions mt-3">
                        <ul class="instruction-list">
                          <li><strong>{{ isThai ? 'เข้าเมนู History (ประวัติ):' : 'Access History Menu:' }}</strong> {{ isThai ? 'คลิกที่เมนู "History" ด้านบน เพื่อดูรายการใบแจ้งซ่อมทั้งหมดของท่าน' : 'Click "History" on the top navigation bar to view your submitted tickets.' }}</li>
                          <li><strong>{{ isThai ? 'สถานะใบงาน 3 ระดับ:' : '3 Major Ticket Statuses:' }}</strong>
                            <div class="d-flex flex-wrap gap-2 mt-1">
                              <span class="badge badge-secondary mr-2">{{ isThai ? 'รอรับเรื่อง (Pending)' : 'Pending' }}</span>
                              <span class="badge badge-primary mr-2">{{ isThai ? 'กำลังดำเนินการ (In Progress)' : 'In Progress' }}</span>
                              <span class="badge badge-success mr-2">{{ isThai ? 'ซ่อมเสร็จสิ้น (Completed)' : 'Completed' }}</span>
                            </div>
                          </li>
                          <li class="mt-2"><strong>{{ isThai ? 'ประเมินความพึงพอใจ (Rating):' : 'Satisfaction Rating:' }}</strong> {{ isThai ? 'เมื่อช่างดำเนินการซ่อมเสร็จ จะมีปุ่มให้ท่านกดประเมินคะแนน 1-5 ดาว และติชมการให้บริการ' : 'Once finished, submit a 1 to 5 star rating to close the service loop.' }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Section C: Quick Tips & FAQ -->
              <div class="tips-faq-section">
                <div class="d-flex align-items-center mb-3">
                  <div class="section-badge mr-2">Tips</div>
                  <h5 class="manual-section-title mb-0">
                    {{ isThai ? 'ข้อแนะนำเพิ่มเติมและข้อควรทราบ (Helpful Tips)' : 'Helpful Tips & Emergency Notes' }}
                  </h5>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="tip-card p-3 h-100">
                      <div class="d-flex align-items-center mb-2">
                        <CIcon name="cil-warning" class="text-danger mr-2" size="lg"/>
                        <strong class="text-dark">{{ isThai ? 'กรณีเกิดเหตุฉุกเฉินเร่งด่วน' : 'Urgent & Emergency Cases' }}</strong>
                      </div>
                      <p class="small text-muted mb-0">
                        {{ isThai 
                          ? 'หากเป็นเหตุฉุกเฉิน เช่น ท่อประปาแตกน้ำท่วมขัง ไฟฟ้าลัดวงจร หรือกลิ่นไหม้ ให้แจ้งซ่อมผ่านระบบและประสานงานโทรแจ้งเจ้าหน้าที่เวรประจำอาคารทันที' 
                          : 'For immediate hazards (pipe bursts, power sparks, burning smells), submit a request and immediately call the building supervisor on duty.' }}
                      </p>
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <div class="tip-card p-3 h-100">
                      <div class="d-flex align-items-center mb-2">
                        <CIcon name="cil-clock" class="text-primary mr-2" size="lg"/>
                        <strong class="text-dark">{{ isThai ? 'การประสานงานและนัดหมาย' : 'Coordination & Onsite Access' }}</strong>
                      </div>
                      <p class="small text-muted mb-0">
                        {{ isThai 
                          ? 'ช่างจะโทรติดต่อตามเบอร์โทรศัพท์ที่ท่านระบุไว้ก่อนเข้าพื้นที่ กรณีเป็นห้องพักหรือห้องส่วนตัว ต้องมีผู้พักอาศัยหรือตัวแทนอยู่ภายในห้องระหว่างการซ่อม' 
                          : 'Technicians will call your registered phone number prior to arrival. For dormitory rooms, someone must be present during the repair.' }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </CTab>

          <!-- 2. SUPERVISOR TAB (For Admin/Supervisor) -->
          <CTab :title="$t('userManual.tabSupervisor')" v-if="userRole === 'admin' || userRole === 'supervisor'">
            <div class="manual-supervisor-container mt-3">
              
              <!-- Section A: 4-Step High-level Workflow for Supervisor -->
              <div class="workflow-summary-section mb-5">
                <div class="d-flex align-items-center mb-3">
                  <div class="section-badge mr-2">Overview</div>
                  <h5 class="manual-section-title mb-0">
                    {{ isThai ? 'ขั้นตอนการดำเนินงานของหัวหน้างาน (Supervisor Workflow)' : 'Supervisor Maintenance Workflow' }}
                  </h5>
                </div>
                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'ภาพรวมขั้นตอนการบริหารจัดการใบแจ้งซ่อม ตั้งแต่การคัดกรอง มอบหมายงานช่าง การประสานส่งต่องาน จนถึงการตรวจรับและปิดงาน' 
                    : 'End-to-end supervisor workflow from triage, assignment, inter-department forwarding, to job closure.' }}
                </p>

                <div class="flowchart-container">
                  <div class="flow-step">
                    <div class="flow-step-num">01</div>
                    <div class="flow-icon bg-warning text-dark"><CIcon name="cil-bell" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '1. ตรวจสอบใบแจ้งซ่อม' : '1. Review Requests' }}</div>
                    <div class="flow-desc">{{ isThai ? 'คัดกรองงานจากแดชบอร์ด ตรวจสอบความถูกต้องและระดับความเร่งด่วน SLA' : 'Triage new tickets from dashboard and check SLA deadlines' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  
                  <div class="flow-step">
                    <div class="flow-step-num">02</div>
                    <div class="flow-icon bg-primary"><CIcon name="cil-user-follow" size="lg" class="text-white"/></div>
                    <div class="flow-title">{{ isThai ? '2. มอบงานช่าง / ส่งต่อ' : '2. Assign / Forward' }}</div>
                    <div class="flow-desc">{{ isThai ? 'จ่ายงานให้ช่างที่รับผิดชอบ หรือโอนย้ายงานไปยังหน่วยงานอื่นที่ตรงสายงาน' : 'Assign to specialized technicians or forward to other departments' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  
                  <div class="flow-step">
                    <div class="flow-step-num">03</div>
                    <div class="flow-icon bg-info"><CIcon name="cil-clock" size="lg" class="text-white"/></div>
                    <div class="flow-title">{{ isThai ? '3. ติดตามสถานะงานซ่อม' : '3. Track Progress' }}</div>
                    <div class="flow-desc">{{ isThai ? 'ติดตามการทำงานของช่าง บันทึกการใช้อะไหล่ และกำกับให้เสร็จตามกำหนด' : 'Monitor on-site repairs, spare parts usage, and SLA performance' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  
                  <div class="flow-step">
                    <div class="flow-step-num">04</div>
                    <div class="flow-icon bg-success"><CIcon name="cil-check-circle" size="lg" class="text-white"/></div>
                    <div class="flow-title">{{ isThai ? '4. ตรวจรับและปิดงาน' : '4. Inspect & Close' }}</div>
                    <div class="flow-desc">{{ isThai ? 'ตรวจสอบรายงานผลการซ่อม รูปภาพหลังซ่อมเสร็จ และปิดงานในระบบ' : 'Inspect completion reports, after-photos, and close ticket' }}</div>
                  </div>
                </div>
              </div>

              <!-- ================= WORKFLOW 1: HOW TO ASSIGN TECHNICIAN ================= -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 1</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'วิธีการมอบหมายงานให้ช่าง (Assigning Technician)' : 'How to Assign Technician to Job' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [1,2,3,4,5,6,7]" 
                      :key="'chip-sup-assign-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeSupStep === s }"
                      @click="highlightSupStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'คำแนะนำขั้นตอนการเลือกใบงานที่รอจัดการจากแดชบอร์ด การเลือกช่างผู้รับผิดชอบ และการกดยืนยันการมอบหมายงานผ่านหน้าต่างจริงของระบบ' 
                    : 'Step-by-step instructions on selecting pending tickets from dashboard, picking responsible technician(s), and confirming assignment.' }}
                </p>

                <!-- Part 1: Dashboard Selection -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper sup-dash">
                      <img 
                        :src="supImgDashboard" 
                        :alt="isThai ? 'ภาพหน้าจอแดชบอร์ดงานที่ต้องจัดการ' : 'Dashboard Pending Tasks Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(supImgDashboard, isThai ? 'หน้าแดชบอร์ด: งานที่ต้องจัดการและปุ่มมอบหมายช่าง' : 'Dashboard: Pending Tasks & Assign Button')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 470" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-sup1" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-sup1" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-sup1" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 1: เลือกใบงานที่รอจัดการ (ชี้ตรงไปยังแถวที่ 4 FIX003082) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupStep === 1 }" @click="highlightSupStep(1)">
                          <path d="M 80 378 L 70 422" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup1)"/>
                          <g filter="url(#glow-shadow-sup1)">
                            <rect x="60" y="350" width="280" height="28" rx="14" class="callout-pill"/>
                            <circle cx="75" cy="364" r="10" class="pin-badge"/>
                            <text x="75" y="368" class="pin-text">1</text>
                            <text x="92" y="368" class="callout-label">
                              {{ isThai ? 'เลือกใบแจ้งซ่อมที่รอจัดการ *' : 'Select Pending Ticket *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 2: คลิกปุ่ม มอบหมายช่าง (ชี้ตรงเข้ากลางปุ่มสีน้ำเงิน) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupStep === 2 }" @click="highlightSupStep(2)">
                          <path d="M 798 398 L 798 430" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup1)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sup1)">
                            <rect x="680" y="370" width="240" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="695" cy="384" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="695" y="388" class="pin-text">2</text>
                            <text x="712" y="388" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "มอบหมายช่าง" *' : 'Click "Assign Technician" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 1 - 2 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-1" :class="{ active: activeSupStep === 1 }" @mouseenter="activeSupStep = 1" @mouseleave="activeSupStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">1</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'เลือกใบแจ้งซ่อมที่รอจัดการ' : 'Select Pending Request' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'ในหน้าแดชบอร์ด ดูรายการในตาราง "งานที่ต้องจัดการ" ตรวจสอบรหัสใบแจ้งซ่อม ปัญหา และเวลา SLA ที่คงเหลือ แล้วคลิกที่แถวของใบแจ้งซ่อมนั้น' 
                                : 'On Dashboard, check "Pending Tasks" table. Review Ticket ID, issue, and SLA remaining, then click on the ticket row.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-info" class="text-info mr-1"/>
                              <small><strong>{{ isThai ? 'สังเกต:' : 'Note:' }}</strong> {{ isThai ? 'ป้ายสีเหลือง "รอดำเนินการ" และเวลานับถอยหลัง SLA ช่วยให้จัดลำดับความเร่งด่วนได้แม่นยำ' : 'Yellow badge indicates ticket is awaiting supervisor assignment.' }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-2" :class="{ active: activeSupStep === 2 }" @mouseenter="activeSupStep = 2" @mouseleave="activeSupStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">2</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิกปุ่ม "มอบหมายช่าง"' : 'Click "Assign Technician"' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'ดำเนินการ' : 'Action' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'เมื่อคลิกเลือกใบงานแล้ว แผงด้านขวามือจะแสดง "รายละเอียดที่เลือก" ตรวจสอบสถานที่และอาการเสีย จากนั้นคลิกปุ่มสีน้ำเงิน "มอบหมายช่าง" ที่ด้านล่าง' 
                                : 'The right sidebar displays full ticket details. Verify location and category, then click the blue "Assign Technician" button at the bottom.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-touch-app" class="text-primary mr-1"/>
                              <small><strong>{{ isThai ? 'ทางลัด:' : 'Shortcut:' }}</strong> {{ isThai ? 'สามารถคลิกปุ่มลูกศรวงกลมทางขวาสุดของแถวในตารางเพื่อเปิดหน้ารายละเอียดได้เช่นกัน' : 'You can also click the circle arrow at the end of the row.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Part 2: Assign Dropdown -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper sup-dropdown">
                      <img 
                        :src="supImgAssignDropdown" 
                        :alt="isThai ? 'ภาพหน้าจอกรอกข้อมูลมอบหมายช่าง' : 'Technician Assignment Form Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(supImgAssignDropdown, isThai ? 'หน้าฟอร์มมอบหมายงาน: เมนูเลือกช่างและปุ่มเพิ่มช่าง' : 'Assignment Form: Dropdown & Add Tech Button')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 644" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-sup2" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-sup2" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-sup2" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 3: เลือกช่างผู้รับผิดชอบหลัก (ชี้เข้ากลางกล่องดรอปดาวน์เลือกช่างที่ y=482) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupStep === 3 }" @click="highlightSupStep(3)">
                          <path d="M 480 433 L 480 472" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup2)"/>
                          <g filter="url(#glow-shadow-sup2)">
                            <rect x="350" y="405" width="260" height="28" rx="14" class="callout-pill"/>
                            <circle cx="365" cy="419" r="10" class="pin-badge"/>
                            <text x="365" y="423" class="pin-text">3</text>
                            <text x="382" y="423" class="callout-label">
                              {{ isThai ? 'เลือกช่างผู้รับผิดชอบหลัก *' : 'Select Lead Technician *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 4: ปุ่มเพิ่มช่าง (ชี้เข้ากลางปุ่ม + เพิ่มช่างที่ x=102, y=514) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupStep === 4 }" @click="highlightSupStep(4)">
                          <path d="M 208 514 L 135 514" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup2)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sup2)">
                            <rect x="210" y="500" width="240" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="225" cy="514" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="225" y="518" class="pin-text">4</text>
                            <text x="242" y="518" class="callout-label">
                              {{ isThai ? 'ปุ่ม "+ เพิ่มช่าง" (กรณีมีช่างร่วม)' : '"+ Add Tech" (Co-workers)' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 5: ปุ่มมอบหมาย (ชี้เข้ากลางปุ่มกรุณาเลือกช่างก่อน / มอบหมายที่ x=135, y=548) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupStep === 5 }" @click="highlightSupStep(5)">
                          <path d="M 258 558 L 205 554" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup2)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sup2)">
                            <rect x="260" y="544" width="220" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="275" cy="558" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="275" y="562" class="pin-text">5</text>
                            <text x="292" y="562" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "มอบหมาย" *' : 'Click "Assign" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 3 - 5 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-lg-4 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-3" :class="{ active: activeSupStep === 3 }" @mouseenter="activeSupStep = 3" @mouseleave="activeSupStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">3</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'เลือกช่างผู้รับผิดชอบหลัก' : 'Select Lead Technician' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'คลิกที่กล่องดรอปดาวน์ "-- กรุณาเลือกช่าง --" เพื่อเลือกช่างที่มีทักษะตรงกับงาน และสังเกตจำนวนงานปัจจุบันของช่างเพื่อเฉลี่ยภาระงานให้เหมาะสม' 
                                : 'Click dropdown to select technician. Check their current active workload tags to balance distribution.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-user" class="text-primary mr-1"/>
                              <small><strong>{{ isThai ? 'ข้อแนะนำ:' : 'Tip:' }}</strong> {{ isThai ? 'ระบบจะแสดงสถานะ "กำลังปฏิบัติงาน X งาน" ต่อท้ายชื่อช่างแต่ละท่าน' : 'Workload count helps avoid assigning overburdened techs.' }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="col-lg-4 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-4" :class="{ active: activeSupStep === 4 }" @mouseenter="activeSupStep = 4" @mouseleave="activeSupStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">4</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'เพิ่มช่างร่วม (ถ้ามี)' : 'Add Co-Technicians (Optional)' }}</strong>
                              <span class="badge badge-secondary ml-auto small font-weight-bold">{{ isThai ? 'ตัวเลือก' : 'Optional' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'หากงานซ่อมมีขนาดใหญ่หรือต้องใช้นั่งร้าน/เครื่องมือพิเศษที่ต้องทำเป็นทีม ให้คลิกปุ่ม "+ เพิ่มช่าง" เพื่อระบุรายชื่อช่างร่วมปฏิบัติงานเพิ่มเติม' 
                                : 'For heavy repairs requiring teamwork, click "+ Add Tech" to attach co-technicians to the same ticket.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-people" class="text-success mr-1"/>
                              <small><strong>{{ isThai ? 'การประสานงาน:' : 'Teamwork:' }}</strong> {{ isThai ? 'ช่างทุกคนที่ได้รับมอบหมายจะสามารถดูใบงานและช่วยบันทึกการซ่อมได้' : 'All assigned techs have access to view and update the job.' }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="col-lg-4 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-5" :class="{ active: activeSupStep === 5 }" @mouseenter="activeSupStep = 5" @mouseleave="activeSupStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">5</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิกปุ่ม "มอบหมาย"' : 'Click "Assign"' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'ดำเนินการ' : 'Action' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'เมื่อเลือกช่างเรียบร้อยแล้ว ปุ่มสีเทา "กรุณาเลือกช่างก่อน" จะเปลี่ยนเป็นปุ่มสีน้ำเงิน "มอบหมาย" ให้คลิกปุ่มเพื่อดำเนินการขั้นตอนถัดไป' 
                                : 'Once a technician is chosen, the button unlocks into a blue "Assign" button. Click to proceed.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-check" class="text-primary mr-1"/>
                              <small><strong>{{ isThai ? 'ความปลอดภัย:' : 'Security:' }}</strong> {{ isThai ? 'ระบบยังไม่ส่งคำสั่งจนกว่าท่านจะกดยืนยันในหน้าต่าง Pop-up ถัดไป' : 'A confirmation modal will appear to prevent accidental clicks.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Part 3: Confirm Modal -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper sup-confirm">
                      <img 
                        :src="supImgAssignConfirm" 
                        :alt="isThai ? 'ภาพหน้าต่างป๊อปอัปยืนยันการมอบหมายงาน' : 'Assignment Confirmation Modal Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(supImgAssignConfirm, isThai ? 'หน้าต่างยืนยันการมอบหมายงาน' : 'Assignment Confirmation Modal')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 646" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-sup3" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-sup3" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-sup3" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 6: ตรวจสอบชื่อช่าง (ชี้ตรงไปยังชื่อช่างในข้อความป๊อปอัป) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupStep === 6 }" @click="highlightSupStep(6)">
                          <path d="M 505 168 L 505 332" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup3)"/>
                          <g filter="url(#glow-shadow-sup3)">
                            <rect x="350" y="140" width="310" height="28" rx="14" class="callout-pill"/>
                            <circle cx="365" cy="154" r="10" class="pin-badge"/>
                            <text x="365" y="158" class="pin-text">6</text>
                            <text x="382" y="158" class="callout-label">
                              {{ isThai ? 'ตรวจสอบชื่อช่างในหน้าต่างยืนยัน *' : 'Verify Technician Name *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 7: ปุ่มยืนยัน สีน้ำเงิน (วางกล่องด้านล่างป๊อปอัป และชี้ขึ้นตรงเข้ากลางปุ่มยืนยัน ไม่ทับปุ่ม) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupStep === 7 }" @click="highlightSupStep(7)">
                          <path d="M 486 480 L 486 424" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup3)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sup3)">
                            <rect x="360" y="480" width="250" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="375" cy="494" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="375" y="498" class="pin-text">7</text>
                            <text x="392" y="498" class="callout-label">
                              {{ isThai ? 'กดปุ่ม "ยืนยัน" สีน้ำเงิน *' : 'Click Blue "Confirm" Button *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 6 - 7 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-6" :class="{ active: activeSupStep === 6 }" @mouseenter="activeSupStep = 6" @mouseleave="activeSupStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">6</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'ตรวจสอบความถูกต้อง' : 'Verify Assignment Details' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'หน้าต่างป๊อปอัปจะแสดงข้อความ "ยืนยันการมอบหมาย? คุณต้องการมอบหมายงานนี้ให้ [ชื่อช่าง] ใช่หรือไม่?" ให้ตรวจสอบชื่อช่างอีกครั้งเพื่อความถูกต้อง' 
                                : 'The modal asks "Confirm assignment to [Technician Name]?". Double-check the name and scope before completing.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-warning" class="text-warning mr-1"/>
                              <small><strong>{{ isThai ? 'หากต้องการเปลี่ยน:' : 'If changing:' }}</strong> {{ isThai ? 'สามารถกดปุ่ม "ยกเลิก" เพื่อกลับไปเลือกช่างท่านอื่นได้ทันที' : 'Click "Cancel" to return to the form and change technician.' }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-7" :class="{ active: activeSupStep === 7 }" @mouseenter="activeSupStep = 7" @mouseleave="activeSupStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">7</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'กดยืนยันการมอบหมายงาน' : 'Confirm & Dispatch Job' }}</strong>
                              <span class="badge badge-success ml-auto small font-weight-bold">{{ isThai ? 'เสร็จสิ้น' : 'Complete' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'คลิกปุ่มสีน้ำเงิน "ยืนยัน" ระบบจะบันทึกการมอบหมาย อัปเดตสถานะใบงานเป็น "มอบหมายช่างแล้ว" และแจ้งเตือนไปยังช่างผู้รับผิดชอบทันที' 
                                : 'Click blue "Confirm". The status changes to "Technician Assigned" and triggers notification to the technician.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-check-circle" class="text-success mr-1"/>
                              <small><strong>{{ isThai ? 'ผลลัพธ์:' : 'Outcome:' }}</strong> {{ isThai ? 'ใบงานจะถูกย้ายจากแท็บ "งานค้าง/รอจัดการ" ไปยังแท็บ "กำลังดำเนินการ"' : 'Ticket transitions to in-progress tracking.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ================= WORKFLOW 2: HOW TO FORWARD TO OTHER DEPARTMENT ================= -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-info text-white mr-2">Workflow 2</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'วิธีการส่งต่อไปยังหน่วยงานอื่น (Forwarding to Another Department)' : 'How to Forward Ticket to Another Department' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [1,2,3,4]" 
                      :key="'chip-sup-fwd-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeSupFwdStep === s }"
                      @click="highlightSupFwdStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'ขั้นตอนการโอนย้ายใบแจ้งซ่อมในกรณีที่ผู้แจ้งระบุประเภทงานผิด หรือปัญหาอยู่นอกขอบเขตความรับผิดชอบของหน่วยงาน เพื่อส่งต่อให้หน่วยงานที่ถูกต้องดำเนินการ' 
                    : 'Instructions for redirecting misclassified tickets or cross-functional requests to the appropriate responsible department.' }}
                </p>

                <!-- Forward Part 1: Ticket List -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper sup-list">
                      <img 
                        :src="supImgListForward" 
                        :alt="isThai ? 'ภาพหน้ารายการแจ้งซ่อมพร้อมปุ่มส่งงานให้หน่วยงานอื่น' : 'Ticket List with Forward Button Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(supImgListForward, isThai ? 'หน้ารายการแจ้งซ่อม: ปุ่มส่งงานให้หน่วยงานอื่น' : 'Tickets List: Forward Button')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 462" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-sup4" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-sup4" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-sup4" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 1: ใบงานที่ต้องการส่งต่อ (ชี้ตรงเข้าเลขรหัส FIX003082) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupFwdStep === 1 }" @click="highlightSupFwdStep(1)">
                          <path d="M 70 248 L 70 295" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup4)"/>
                          <g filter="url(#glow-shadow-sup4)">
                            <rect x="35" y="220" width="280" height="28" rx="14" class="callout-pill"/>
                            <circle cx="50" cy="234" r="10" class="pin-badge"/>
                            <text x="50" y="238" class="pin-text">1</text>
                            <text x="67" y="238" class="callout-label">
                              {{ isThai ? 'ตรวจสอบใบงานที่ต้องโอนย้าย *' : 'Check Ticket To Transfer *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 2: ปุ่มส่งงานให้หน่วยงานอื่น (ชี้ตรงเข้ากลางปุ่มกรอบน้ำเงิน ส่งงานให้หน่วยงานอื่น) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupFwdStep === 2 }" @click="highlightSupFwdStep(2)">
                          <path d="M 850 248 Q 935 248 935 282" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup4)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sup4)">
                            <rect x="670" y="220" width="280" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="685" cy="234" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="685" y="238" class="pin-text">2</text>
                            <text x="702" y="238" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "ส่งงานให้หน่วยงานอื่น" *' : 'Click "Forward Department" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 1 - 2 (Forward) -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-fwd-1" :class="{ active: activeSupFwdStep === 1 }" @mouseenter="activeSupFwdStep = 1" @mouseleave="activeSupFwdStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">1</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'ตรวจสอบใบงานที่ไม่ตรงกับหน่วยงาน' : 'Inspect Misrouted Ticket' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'ในหน้าหลัก "รายการแจ้งซ่อม — งานที่เข้าใหม่" ตรวจสอบรายละเอียดงานและฝ่าย/แผนก หากพบว่าเป็นงานที่อยู่นอกเหนือความรับผิดชอบของทีมท่าน ให้เตรียมส่งต่องาน' 
                                : 'Under Tickets List, review department and problem description. If outside your team scope, prepare to forward.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-share-alt" class="text-info mr-1"/>
                              <small><strong>{{ isThai ? 'เหตุผล:' : 'Reason:' }}</strong> {{ isThai ? 'การส่งต่อที่รวดเร็วช่วยลดระยะเวลาตกค้างของงานและทำให้ผู้แจ้งได้รับการบริการที่รวดเร็ว' : 'Prompt forwarding ensures rapid response and minimizes ticket idle time.' }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-fwd-2" :class="{ active: activeSupFwdStep === 2 }" @mouseenter="activeSupFwdStep = 2" @mouseleave="activeSupFwdStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">2</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิก "ส่งงานให้หน่วยงานอื่น"' : 'Click "Forward Department"' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'ดำเนินการ' : 'Action' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'คลิกปุ่มกรอบสีน้ำเงิน "ส่งงานให้หน่วยงานอื่น" ทางขวามือของแถว หรือหากเปิดดูในหน้ารายละเอียดอยู่ ให้คลิกปุ่ม "ส่งต่อไปยังหน่วยงานอื่น" ที่มุมบนขวา' 
                                : 'Click outline button "Forward to Other Department" on the row, or top-right button inside the ticket detail view.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-external-link" class="text-primary mr-1"/>
                              <small><strong>{{ isThai ? 'ตำแหน่งปุ่ม:' : 'Locations:' }}</strong> {{ isThai ? 'ปุ่มมีให้กดได้ทั้งจากหน้ารายการตาราง และมุมบนขวาของหน้ารายละเอียดงาน' : 'Available both in the table row and at the top right of ticket detail page.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Forward Part 2: Department Selection & Confirm -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper sup-forward">
                      <img 
                        :src="supImgForwardDept" 
                        :alt="isThai ? 'ภาพกล่องเลือกหน่วยงานปลายทางเพื่อส่งต่องาน' : 'Target Department Selection Form Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(supImgForwardDept, isThai ? 'กล่องเลือกหน่วยงานปลายทางและปุ่มยืนยัน' : 'Department Selection & Confirm Box')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 652" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-sup5" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-sup5" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-sup5" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 3: เลือกหน่วยงานปลายทาง (ชี้ตรงเข้ากลางกล่องดรอปดาวน์เลือกหน่วยงาน) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupFwdStep === 3 }" @click="highlightSupFwdStep(3)">
                          <path d="M 494 128 L 494 185" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup5)"/>
                          <g filter="url(#glow-shadow-sup5)">
                            <rect x="350" y="100" width="288" height="28" rx="14" class="callout-pill"/>
                            <circle cx="365" cy="114" r="10" class="pin-badge"/>
                            <text x="365" y="118" class="pin-text">3</text>
                            <text x="382" y="118" class="callout-label">
                              {{ isThai ? 'เลือกหน่วยงานปลายทางที่ถูกต้อง *' : 'Select Target Department *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 4: ปุ่มยืนยันการส่งต่อ (ชี้ตรงเข้ากลางปุ่มยืนยันการส่งต่อ) -->
                        <g class="annotation-group" :class="{ 'is-active': activeSupFwdStep === 4 }" @click="highlightSupFwdStep(4)">
                          <path d="M 188 264 L 132 264" class="pointer-arrow" marker-end="url(#arrowhead-blue-sup5)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sup5)">
                            <rect x="190" y="250" width="260" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="205" cy="264" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="205" y="268" class="pin-text">4</text>
                            <text x="222" y="268" class="callout-label">
                              {{ isThai ? 'กดปุ่ม "ยืนยันการส่งต่อ" *' : 'Click "Confirm Forward" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 3 - 4 (Forward) -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-fwd-3" :class="{ active: activeSupFwdStep === 3 }" @mouseenter="activeSupFwdStep = 3" @mouseleave="activeSupFwdStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">3</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'เลือกหน่วยงานปลายทาง' : 'Select Target Department' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'ในแผง "ส่งงานให้หน่วยงานอื่น" คลิกที่ดรอปดาวน์ "-- กรุณาเลือกหน่วยงาน --" และเลือกหน่วยงานที่ถูกต้อง เช่น สำนักวิชา หรือส่วนจัดการทรัพย์สิน' 
                                : 'In forward panel, click dropdown and choose the correct target department corresponding to the repair work.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-building" class="text-primary mr-1"/>
                              <small><strong>{{ isThai ? 'การตรวจสอบ:' : 'Verification:' }}</strong> {{ isThai ? 'โปรดตรวจสอบชื่อหน่วยงานให้ตรงกับลักษณะงานเพื่อป้องกันการส่งต่อซ้ำซ้อน' : 'Double check target unit to prevent redundant re-forwarding.' }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-sup-fwd-4" :class="{ active: activeSupFwdStep === 4 }" @mouseenter="activeSupFwdStep = 4" @mouseleave="activeSupFwdStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">4</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'กดยืนยันการส่งต่อ' : 'Confirm & Complete Forwarding' }}</strong>
                              <span class="badge badge-success ml-auto small font-weight-bold">{{ isThai ? 'เสร็จสิ้น' : 'Complete' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'คลิกปุ่ม "ยืนยันการส่งต่อ" ระบบจะโอนย้ายใบแจ้งซ่อมไปยังคิวงานของหน่วยงานปลายทางทันที พร้อมบันทึกประวัติการส่งต่อว่าถูกส่งมาจากหน่วยงานใด' 
                                : 'Click "Confirm Forward". The ticket transfers to target department queue with complete forwarding audit trail.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-check-circle" class="text-success mr-1"/>
                              <small><strong>{{ isThai ? 'ประวัติงาน:' : 'Audit Trail:' }}</strong> {{ isThai ? 'ระบบจะระบุสถานะ "ถูกส่งต่อมาจากหน่วยงานอื่น" เพื่อให้หัวหน้างานปลายทางทราบที่มา' : 'Tagged with "Forwarded from another department" badge.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section C: Supervisor Quick Tips & Best Practices -->
              <div class="tips-faq-section">
                <div class="d-flex align-items-center mb-3">
                  <div class="section-badge mr-2">Best Practices</div>
                  <h5 class="manual-section-title mb-0">
                    {{ isThai ? 'ข้อควรทราบและแนวทางปฏิบัติที่ดีของหัวหน้างาน (Supervisor Tips)' : 'Supervisor Best Practices & SLA Management' }}
                  </h5>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="tip-card p-3 h-100">
                      <div class="d-flex align-items-center mb-2">
                        <CIcon name="cil-alarm" class="text-danger mr-2" size="lg"/>
                        <strong class="text-dark">{{ isThai ? 'การบริหารจัดการเวลา SLA' : 'SLA Target Management' }}</strong>
                      </div>
                      <p class="small text-muted mb-0">
                        {{ isThai 
                          ? 'ควรจ่ายงานช่างภายใน 2 - 4 ชั่วโมงหลังได้รับแจ้ง เพื่อป้องกันสถานะ "เกิน SLA" หากงานมีความซับซ้อนให้โทรประสานงานผู้แจ้งเพื่อชี้แจงกรอบเวลาล่วงหน้า' 
                          : 'Assign technicians within 2 - 4 hours to avoid SLA breaches. Coordinate with requester if extra parts ordering is necessary.' }}
                      </p>
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <div class="tip-card p-3 h-100">
                      <div class="d-flex align-items-center mb-2">
                        <CIcon name="cil-chart-pie" class="text-primary mr-2" size="lg"/>
                        <strong class="text-dark">{{ isThai ? 'การกระจายงานและสถานะช่าง' : 'Workload Balancing' }}</strong>
                      </div>
                      <p class="small text-muted mb-0">
                        {{ isThai 
                          ? 'ตรวจสอบการ์ด "สถานะช่าง" บนแดชบอร์ดเสมอเพื่อดูจำนวนช่างที่พร้อมปฏิบัติงานและช่างที่ติดงานซ่อม ช่วยให้กระจายงานได้อย่างสมดุลและมีประสิทธิภาพสูงสุด' 
                          : 'Monitor the "Technician Status" card on Dashboard to see idle vs active technicians for optimal workload distribution.' }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </CTab>

          

          <!-- 3. TECHNICIAN TAB (For Admin/Technician) -->
          <CTab :title="$t('userManual.tabTech')" v-if="userRole === 'admin' || userRole === 'technician'">
            <div class="mt-4">
              <h5 class="manual-section-title mb-4">{{ $t('userManual.techHeading') }}</h5>

              <!-- Overview Flowchart for Technician -->
              <div class="flowchart-container mb-5">
                <div class="flow-step">
                  <div class="flow-icon bg-warning text-dark"><CIcon name="cil-task" size="lg"/></div>
                  <div class="flow-title">{{ isThai ? '1. รับแจ้งงาน' : '1. Receive Task' }}</div>
                  <div class="flow-desc">{{ isThai ? 'ตรวจสอบรายการงานและกดรับงาน' : 'Review details & accept task' }}</div>
                </div>
                <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                <div class="flow-step">
                  <div class="flow-icon bg-info"><CIcon name="cil-clock" size="lg" class="text-white"/></div>
                  <div class="flow-title">{{ isThai ? '2. บันทึกเวลาเข้าซ่อม' : '2. Update ETA' }}</div>
                  <div class="flow-desc">{{ isThai ? 'ระบุระยะเวลาเข้าซ่อมโดยประมาณ' : 'Estimate repair arrival time' }}</div>
                </div>
                <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                <div class="flow-step">
                  <div class="flow-icon bg-primary"><CIcon name="cil-settings" size="lg" class="text-white"/></div>
                  <div class="flow-title">{{ isThai ? '3. ปฏิบัติงานซ่อม' : '3. Perform Repair' }}</div>
                  <div class="flow-desc">{{ isThai ? 'แก้ไขปัญหาและใช้อะไหล่ตามจริง' : 'Troubleshoot and fix equipment' }}</div>
                </div>
                <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                <div class="flow-step">
                  <div class="flow-icon bg-success"><CIcon name="cil-check-circle" size="lg" class="text-white"/></div>
                  <div class="flow-title">{{ isThai ? '4. แนบรูป & ปิดงาน' : '4. Photo & Close' }}</div>
                  <div class="flow-desc">{{ isThai ? 'บันทึกผลงาน ถ่ายรูป และยืนยันปิดงาน' : 'Upload photo & confirm completion' }}</div>
                </div>
              </div>

              <!-- ========================================== -->
              <!-- WORKFLOW 1: ตรวจสอบและกดรับงานซ่อม (Ticket Acceptance) -->
              <!-- ========================================== -->
              <div class="step-guide-section mb-5">
                <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 1</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'วิธีการตรวจสอบใบงานและกดรับงานซ่อม (Ticket Acceptance)' : 'Ticket Acceptance Workflow' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [1, 2, 3]" 
                      :key="'chip-tech-accept-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeTechStep === s }"
                      @click="highlightTechStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'ขั้นตอนการเปิดดูรายการใบแจ้งซ่อมที่ได้รับมอบหมายจากหน้าแดชบอร์ด ตรวจสอบรายละเอียดงาน และกดยืนยันการรับงานเพื่อเริ่มดำเนินการ' 
                    : 'Instructions for viewing assigned repair tickets from dashboard, reviewing issue details, and confirming job acceptance.' }}
                </p>

                <!-- Step 1: Dashboard Ticket Selection -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper tech-dash">
                      <img 
                        :src="techImgDashboard" 
                        :alt="isThai ? 'แดชบอร์ดช่าง รายการใบงานที่ได้รับมอบหมาย' : 'Technician Dashboard Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(techImgDashboard, isThai ? 'หน้าแดชบอร์ด: รายละเอียดใบงานที่ได้รับมอบหมาย' : 'Dashboard: Assigned Tickets List')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 480" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-tech1" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-blue-tech1" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 1: คลิกที่เลขที่ใบแจ้งซ่อม -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 1 }" @click="highlightTechStep(1)">
                          <path d="M 65 408 L 40 445" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech1)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech1)">
                            <rect x="25" y="375" width="310" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="40" cy="389" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="40" y="393" class="pin-text">1</text>
                            <text x="57" y="393" class="callout-label">
                              {{ isThai ? 'คลิกที่เลขที่ใบแจ้งซ่อม (FIX003082) *' : 'Click Ticket ID (FIX003082) *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step 1 Card inside annotated-card -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-12 mb-2">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-1" 
                            :class="{ active: activeTechStep === 1 }" 
                            @mouseenter="activeTechStep = 1" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(1)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">1</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิกที่เลขที่ใบแจ้งซ่อมในตารางงาน' : 'Click Ticket ID in Assigned Tickets Table' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'ดำเนินการ' : 'Action' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'เมื่อหัวหน้างานมอบหมายงานให้ท่าน รายการใบงานจะปรากฏในตาราง "รายละเอียดใบงาน" ในสถานะ "มอบหมายช่าง" ให้คลิกที่รหัสใบแจ้งซ่อม (เช่น FIX003082) เพื่อเปิดดูรายละเอียดงาน' 
                                : 'When a supervisor assigns a ticket, it appears in "Assigned Tickets" with status "Assigned". Click the Ticket ID to open full details.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-info" class="text-info mr-1"/>
                              <small><strong>{{ isThai ? 'สังเกต:' : 'Note:' }}</strong> {{ isThai ? 'รายการใบงานจะแสดงประเภทงาน อาการเสีย และเวลานับถอยหลัง SLA เพื่อให้จัดการได้ทันกำหนด' : 'The ticket displays issue type, symptom description, and remaining SLA.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Step 2: Accept Job Button -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper tech-accept">
                      <img 
                        :src="techImgAcceptJob" 
                        :alt="isThai ? 'หน้ารายละเอียดใบงานพร้อมปุ่มรับงาน' : 'Ticket Detail Accept Button Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(techImgAcceptJob, isThai ? 'หน้ารายละเอียดใบงาน: ปุ่มรับงานและปฏิเสธงาน' : 'Ticket Detail: Accept & Reject Buttons')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 678" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-tech2" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-blue-tech2" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 2: คลิกปุ่ม รับงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 2 }" @click="highlightTechStep(2)">
                          <path d="M 465 342 L 465 375" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech2)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech2)">
                            <rect x="320" y="310" width="290" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="335" cy="324" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="335" y="328" class="pin-text">2</text>
                            <text x="352" y="328" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "รับงาน" (สีน้ำเงิน) *' : 'Click "Accept" (Blue Button) *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step 2 Card inside annotated-card -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-12 mb-2">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-2" 
                            :class="{ active: activeTechStep === 2 }" 
                            @mouseenter="activeTechStep = 2" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(2)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">2</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'ตรวจสอบรายละเอียดงานและคลิกปุ่ม "รับงาน"' : 'Review Issue Details & Click Accept' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'ดำเนินการ' : 'Action' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'ตรวจสอบประเภทงาน อาการเสีย สถานที่ และภาพถ่ายปัญหา เมื่อพร้อมปฏิบัติงานให้คลิกปุ่มสีน้ำเงิน "รับงาน"' 
                                : 'Review the issue category, symptom description, location, and photos. When ready, click "Accept".' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-lightbulb" class="text-warning mr-1"/>
                              <small><strong>{{ isThai ? 'กรณีติดภารกิจอื่น:' : 'If unavailable:' }}</strong> {{ isThai ? 'หากติดงานด่วนอื่นหรือไม่สามารถเข้าซ่อมได้ สามารถคลิกปุ่ม "ปฏิเสธงาน" (ปุ่มขอบสีแดง) เพื่อส่งต่อให้หัวหน้างานมอบหมายช่างท่านอื่น' : 'You may click "Reject" (red button) if you are unable to handle this job.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Step 3: Confirm Accept Modal -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper tech-confirm">
                      <img 
                        :src="techImgConfirmAccept" 
                        :alt="isThai ? 'หน้าต่างป๊อปอัปยืนยันการรับงาน' : 'Accept Job Modal Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(techImgConfirmAccept, isThai ? 'หน้าต่างยืนยันรับงาน' : 'Accept Job Confirmation Modal')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 667" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-tech3" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-blue-tech3" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 3: คลิกปุ่มยืนยันรับงาน (ลูกศรชี้ขึ้น) -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 3 }" @click="highlightTechStep(3)">
                          <path d="M 489 480 L 489 422" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech3)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech3)">
                            <rect x="345" y="482" width="285" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="360" cy="496" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="360" y="500" class="pin-text">3</text>
                            <text x="377" y="500" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "รับงาน" เพื่อยืนยัน *' : 'Click "Accept" to Confirm *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step 3 Card inside annotated-card -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-12 mb-2">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-3" 
                            :class="{ active: activeTechStep === 3 }" 
                            @mouseenter="activeTechStep = 3" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(3)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">3</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิกปุ่มยืนยันรับงานในหน้าต่างป๊อปอัป' : 'Confirm Acceptance in Popup Window' }}</strong>
                              <span class="badge badge-success ml-auto small font-weight-bold">{{ isThai ? 'ยืนยัน' : 'Confirm' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'ระบบจะแสดงหน้าต่างยืนยัน "คุณต้องการรับงานนี้ใช่หรือไม่?" ให้คลิกปุ่มสีน้ำเงิน "รับงาน" สถานะจะเปลี่ยนเป็น "กำลังดำเนินการ" ทันที' 
                                : 'A confirmation modal will appear. Click the blue "Accept" button. The ticket status updates to "In Progress".' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-check-circle" class="text-success mr-1"/>
                              <small><strong>{{ isThai ? 'สังเกต:' : 'Note:' }}</strong> {{ isThai ? 'หลังจากกดยืนยันรับงาน ระบบจะนำท่านเข้าสู่ขั้นตอนการระบุเวลาเข้าซ่อม (ETA) โดยอัตโนมัติ' : 'After confirming, the system directs you to specify your ETA automatically.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ========================================== -->
              <!-- WORKFLOW 2: อัปเดตเวลาเข้าซ่อม (ETA) และปิดงาน (Completion) -->
              <!-- ========================================== -->
              <div class="step-guide-section mb-5">
                <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 2</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การบันทึกเวลาเข้าซ่อม (ETA) และการบันทึกผลงานปิดงาน (ETA Update & Job Completion)' : 'ETA Estimation & Job Completion Workflow' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [4, 5, 6, 7, 8, 9]" 
                      :key="'chip-tech-comp-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeTechStep === s }"
                      @click="highlightTechStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'เมื่อรับงานแล้ว ช่างต้องระบุระยะเวลาเข้าซ่อมโดยประมาณ (ETA) เพื่อให้ผู้แจ้งทราบ และเมื่อทำการซ่อมแซมเสร็จสิ้น ให้แนบรูปถ่าย อธิบายการแก้ไข และกดยืนยันปิดงาน' 
                    : 'Technicians must provide an ETA to notify the requester. After completing repairs, upload completion photos, enter repair notes, and confirm job closure.' }}
                </p>

                <!-- Step 4 & 5: ETA Update -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper tech-eta">
                      <img 
                        :src="techImgUpdateEta" 
                        :alt="isThai ? 'หน้าต่างระบุระยะเวลาเข้าซ่อม ETA' : 'ETA Estimation Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(techImgUpdateEta, isThai ? 'หน้าระบุระยะเวลาเข้าซ่อมโดยประมาณ (ETA)' : 'ETA Estimation Screen')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 671" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-tech4" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-blue-tech4" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 4: เลือกระยะเวลาซ่อมโดยประมาณ -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 4 }" @click="highlightTechStep(4)">
                          <path d="M 430 312 L 540 365" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech4)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech4)">
                            <rect x="280" y="280" width="295" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="295" cy="294" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="295" y="298" class="pin-text">4</text>
                            <text x="312" y="298" class="callout-label">
                              {{ isThai ? 'เลือกระยะเวลาซ่อมโดยประมาณ (ETA) *' : 'Select ETA Option *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 5: คลิกปุ่ม บันทึก -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 5 }" @click="highlightTechStep(5)">
                          <path d="M 720 312 L 675 350" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech4)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech4)">
                            <rect x="690" y="280" width="180" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="705" cy="294" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="705" y="298" class="pin-text">5</text>
                            <text x="722" y="298" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "บันทึก" *' : 'Click "Save" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step 4 & 5 Cards inside annotated-card -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-4" 
                            :class="{ active: activeTechStep === 4 }" 
                            @mouseenter="activeTechStep = 4" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(4)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">4</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'เลือกระยะเวลาซ่อมโดยประมาณ (ETA)' : 'Select Estimated Repair Time' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'คลิกเลือกตัวเลือกที่ตรงกับสถานการณ์จริง เช่น "ดำเนินการซ่อมได้เลย (ภายใน 30 นาที / 1 ชั่วโมง)" หรือ "รออะไหล่" หรือ "เลื่อนซ่อม"' 
                                : 'Choose the appropriate option: Immediate repair, Awaiting spare parts, or Rescheduled.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-info" class="text-info mr-1"/>
                              <small><strong>{{ isThai ? 'สังเกต:' : 'Note:' }}</strong> {{ isThai ? 'การระบุ ETA ช่วยให้ผู้แจ้งทราบกำหนดเวลาที่แน่นอนและลดการติดตามทวงถาม' : 'ETA helps inform the requester of arrival time.' }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="col-md-6 mb-3">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-5" 
                            :class="{ active: activeTechStep === 5 }" 
                            @mouseenter="activeTechStep = 5" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(5)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">5</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิกปุ่ม "บันทึก"' : 'Click "Save" Button' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'ดำเนินการ' : 'Action' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'คลิกปุ่ม "บันทึก" เพื่ออัปเดตเวลาเข้าซ่อมเข้าสู่ระบบ ระบบจะส่งการแจ้งเตือนเวลาเข้าซ่อมไปยังผู้แจ้งทันที' 
                                : 'Click "Save". The estimated time is recorded and sent to the requester.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-touch-app" class="text-primary mr-1"/>
                              <small><strong>{{ isThai ? 'คำแนะนำ:' : 'Tip:' }}</strong> {{ isThai ? 'หากมีเหตุจำเป็นต้องเลื่อนเวลา สามารถกลับมาแก้ไข ETA ได้ตลอดเวลาก่อนปิดงาน' : 'You can update ETA again if there are unforeseen delays.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Step 6, 7, 8, 9: Job Completion -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper tech-complete">
                      <img 
                        :src="techImgCompleteJob" 
                        :alt="isThai ? 'หน้าต่างกรอกรายละเอียดผลการซ่อมและยืนยันปิดงาน' : 'Job Completion Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(techImgCompleteJob, isThai ? 'หน้าบันทึกผลงานหลังซ่อมและยืนยันปิดงาน' : 'Job Completion Form Screen')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 678" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-tech5" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-blue-tech5" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 6: แนบรูปภาพผลงานหลังซ่อมเสร็จ -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 6 }" @click="highlightTechStep(6)">
                          <path d="M 215 260 L 256 318" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech5)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech5)">
                            <rect x="90" y="232" width="250" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="105" cy="246" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="105" y="250" class="pin-text">6</text>
                            <text x="122" y="250" class="callout-label">
                              {{ isThai ? 'แนบรูปภาพผลงานหลังซ่อม *' : 'Upload Completion Photo *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 7: อธิบายสิ่งที่ได้ดำเนินการซ่อมแซม -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 7 }" @click="highlightTechStep(7)">
                          <path d="M 650 260 L 650 310" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech5)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech5)">
                            <rect x="520" y="232" width="270" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="535" cy="246" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="535" y="250" class="pin-text">7</text>
                            <text x="552" y="250" class="callout-label">
                              {{ isThai ? 'ระบุรายละเอียดการซ่อม/แก้ไข *' : 'Enter Repair Details *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 8: ระบุรายการอะไหล่ หรือไม่ใช้อะไหล่ -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 8 }" @click="highlightTechStep(8)">
                          <path d="M 260 446 L 190 446" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech5)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech5)">
                            <rect x="260" y="432" width="290" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="275" cy="446" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="275" y="450" class="pin-text">8</text>
                            <text x="292" y="450" class="callout-label">
                              {{ isThai ? 'ระบุรายการอะไหล่ หรือติ๊ก "ไม่ใช้อะไหล่"' : 'Specify Spare Parts or Check None' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 9: คลิกปุ่ม ยืนยันการปิดงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeTechStep === 9 }" @click="highlightTechStep(9)">
                          <path d="M 514 622 L 514 593" class="pointer-arrow" marker-end="url(#arrowhead-blue-tech5)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-tech5)">
                            <rect x="370" y="622" width="285" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="385" cy="636" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="385" y="640" class="pin-text">9</text>
                            <text x="402" y="640" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "ยืนยันการปิดงาน" *' : 'Click "Confirm Job Closure" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step 6, 7, 8, 9 Cards inside annotated-card -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <!-- Step 6 -->
                        <div class="col-md-6 mb-3">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-6" 
                            :class="{ active: activeTechStep === 6 }" 
                            @mouseenter="activeTechStep = 6" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(6)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">6</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'แนบรูปภาพผลงานหลังซ่อมเสร็จ' : 'Upload Completion Photos' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'ถ่ายรูปหรืออัปโหลดภาพผลงานหลังซ่อมเสร็จเพื่อเป็นหลักฐานยืนยันความเรียบร้อย (รองรับไฟล์รูปภาพ JPG, PNG)' 
                                : 'Take or upload photos showing the completed repair work as proof of resolution.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-camera" class="text-info mr-1"/>
                              <small><strong>{{ isThai ? 'สังเกต:' : 'Note:' }}</strong> {{ isThai ? 'ควรถ่ายภาพให้เห็นอุปกรณ์ที่ซ่อมแซมเสร็จในมุมมองกว้างและชัดเจน' : 'Take clear, wide-angle photos of the repaired equipment.' }}</small>
                            </div>
                          </div>
                        </div>

                        <!-- Step 7 -->
                        <div class="col-md-6 mb-3">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-7" 
                            :class="{ active: activeTechStep === 7 }" 
                            @mouseenter="activeTechStep = 7" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(7)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">7</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'อธิบายสิ่งที่ได้ดำเนินการซ่อมแซม' : 'Enter Repair Actions Taken' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'พิมพ์สรุปขั้นตอนและวิธีการแก้ไข เช่น "ล้างแผงคอยล์เย็น เปลี่ยนแคปรัน และเติมน้ำยาแอร์ ทดสอบระบบทำงานปกติแล้ว"' 
                                : 'Summarize the actions taken to fix the issue clearly and concisely.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-pencil" class="text-info mr-1"/>
                              <small><strong>{{ isThai ? 'สังเกต:' : 'Note:' }}</strong> {{ isThai ? 'ข้อความนี้จะแสดงในประวัติการซ่อมและส่งให้ผู้แจ้งตรวจสอบ' : 'Notes will be visible to requester and logged in history.' }}</small>
                            </div>
                          </div>
                        </div>

                        <!-- Step 8 -->
                        <div class="col-md-6 mb-3">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-8" 
                            :class="{ active: activeTechStep === 8 }" 
                            @mouseenter="activeTechStep = 8" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(8)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">8</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'ระบุรายการอะไหล่ หรือติ๊ก "ไม่ใช้อะไหล่"' : 'Spare Parts or Check "No Parts Used"' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'ดำเนินการ' : 'Action' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'หากมีการเบิกใช้อะไหล่ ให้ระบุชื่ออะไหล่และจำนวนที่ใช้จริง หรือหากไม่มีการเปลี่ยนอะไหล่ให้ติ๊กเลือกช่อง "ไม่ใช้อะไหล่"' 
                                : 'List any spare parts consumed during repair, or check "No spare parts used".' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-info" class="text-info mr-1"/>
                              <small><strong>{{ isThai ? 'สังเกต:' : 'Note:' }}</strong> {{ isThai ? 'การบันทึกอะไหล่ที่ถูกต้องจะนำไปตัดสต็อกในคลังพัสดุอัตโนมัติ' : 'Parts logged will be deducted from inventory automatically.' }}</small>
                            </div>
                          </div>
                        </div>

                        <!-- Step 9 -->
                        <div class="col-md-6 mb-3">
                          <div 
                            class="step-item-card h-100" 
                            id="step-card-tech-9" 
                            :class="{ active: activeTechStep === 9 }" 
                            @mouseenter="activeTechStep = 9" 
                            @mouseleave="activeTechStep = null"
                            @click="highlightTechStep(9)"
                          >
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">9</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิกปุ่ม "ยืนยันการปิดงาน"' : 'Confirm Job Closure' }}</strong>
                              <span class="badge badge-success ml-auto small font-weight-bold">{{ isThai ? 'เสร็จสิ้น' : 'Finish' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'ตรวจสอบความถูกต้องครบถ้วนแล้วคลิกปุ่มสีน้ำเงิน "ยืนยันการปิดงาน" สถานะใบงานจะเปลี่ยนเป็น "ปิดงานแล้ว" พร้อมส่งให้ผู้แจ้งประเมินความพึงพอใจ' 
                                : 'Click the blue "Confirm Job Closure" button. Status becomes "Completed" and requests requester evaluation.' }}
                            </p>
                            <div class="step-tip-box">
                              <CIcon name="cil-check-circle" class="text-success mr-1"/>
                              <small><strong>{{ isThai ? 'ผลลัพธ์:' : 'Result:' }}</strong> {{ isThai ? 'ใบงานจะถูกบันทึกสำเร็จและย้ายไปอยู่ในแท็บ "งานที่เสร็จสิ้น"' : 'Job is saved and moved to "Completed" list.' }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Technician Best Practices Box -->
                <div class="card border-info bg-light mb-4 mt-4">
                  <div class="card-body">
                    <h6 class="text-info font-weight-bold mb-2 d-flex align-items-center">
                      <CIcon name="cil-info" class="mr-2"/> {{ isThai ? 'ข้อควรปฏิบัติสำหรับช่างซ่อมบำรุง' : 'Technician Best Practices' }}
                    </h6>
                    <ul class="text-muted small mb-0 pl-3">
                      <li class="mb-1">
                        <strong>{{ isThai ? 'กดรับงานและอัปเดต ETA ทันที:' : 'Accept and update ETA promptly:' }}</strong>
                        {{ isThai 
                          ? 'ควรกดรับงานภายใน 1 - 2 ชั่วโมงหลังได้รับมอบหมาย เพื่อไม่ให้ใบงานเกินเวลา SLA' 
                          : 'Accept and set ETA within 1 - 2 hours to avoid breaching SLA.' }}
                      </li>
                      <li class="mb-1">
                        <strong>{{ isThai ? 'รูปภาพผลงานหลังซ่อมเสร็จ:' : 'Clear completion photo:' }}</strong>
                        {{ isThai 
                          ? 'ควรถ่ายภาพให้เห็นอุปกรณ์ที่ซ่อมแซมเสร็จในมุมมองกว้างและชัดเจน เพื่อความโปร่งใสในการตรวจสอบ' 
                          : 'Take high-clarity photos of the repaired equipment for transparent verification.' }}
                      </li>
                      <li>
                        <strong>{{ isThai ? 'การระบุอะไหล่:' : 'Accurate spare parts logging:' }}</strong>
                        {{ isThai 
                          ? 'การบันทึกอะไหล่ที่ถูกต้องจะถูกนำไปตัดสต็อกในระบบคลังพัสดุอัตโนมัติ ช่วยลดข้อผิดพลาดในการนับสต็อก' 
                          : 'Accurately logging used parts automatically synchronizes inventory stock.' }}
                      </li>
                    </ul>
                  </div>
                </div>

              </div>
            </div>
          </CTab>

          <!-- 4. STUDENT AFFAIRS TAB (For Admin / Student Affairs) -->
                    <!-- 4. STUDENT AFFAIRS TAB -->
          <CTab :title="$t('userManual.tabStudentAffairs')" v-if="userRole === 'admin' || userRole === 'student_affairs'">
            <div class="manual-student-affairs-container mt-3">
              
              <!-- Section A: 4-Step High-level Workflow for Student Affairs -->
              <div class="workflow-summary-section mb-5">
                <div class="d-flex align-items-center mb-3">
                  <div class="section-badge mr-2">Overview</div>
                  <h5 class="manual-section-title mb-0">
                    {{ isThai ? 'ขั้นตอนการติดตามงานของส่วนพัฒนานักศึกษา (Student Affairs Workflow)' : 'Student Affairs Monitoring Workflow' }}
                  </h5>
                </div>
                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'ภาพรวมบทบาทของส่วนพัฒนานักศึกษา: ติดตามสถานะงานซ่อมของนักศึกษา (หอพักและอาคารเรียน) ผ่านแดชบอร์ด โดยมีสิทธิ์ "ดูได้อย่างเดียว (Read-Only)" เพื่อความโปร่งใสและตรวจสอบความคืบหน้าการให้บริการ' 
                    : 'Overview of Student Affairs role: Monitoring maintenance tickets for student dormitories and facilities. This role is Read-Only for reporting and status tracking.' }}
                </p>

                <!-- Notice callout banner: Read-only role -->
                <div class="alert alert-info border-info d-flex align-items-center mb-4 py-2 px-3 rounded-lg shadow-sm" style="background: #eff6ff; border-left: 5px solid #2563eb !important;">
                  <CIcon name="cil-info" size="lg" class="mr-3 text-primary flex-shrink-0"/>
                  <div class="small">
                    <strong class="text-primary">{{ isThai ? 'ข้อกำหนดสิทธิ์การใช้งาน (Role Permissions):' : 'Role Permissions Notice:' }}</strong>
                    {{ isThai 
                      ? ' บัญชีบทบาทส่วนพัฒนานักศึกษามีสิทธิ์ "ดูข้อมูลเพื่อติดตามสถานะเท่านั้น (View-Only)" ในระบบจะไม่มีปุ่มส่งแจ้งซ่อมใหม่ และไม่มีปุ่มมอบหมายงานให้ช่าง' 
                      : ' Student Affairs accounts have Read-Only permissions for monitoring and reporting. Submitting new requests and assigning technicians are disabled for this role.' }}
                  </div>
                </div>

                <!-- 4 Overview Steps -->
                <div class="flowchart-container">
                  <div class="flow-step">
                    <div class="flow-step-num">01</div>
                    <div class="flow-icon bg-info text-white"><CIcon name="cil-speedometer" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '1. ดูภาพรวมสถิติ' : '1. Overview Stats' }}</div>
                    <div class="flow-desc">{{ isThai ? 'ตรวจสอบยอดคำร้องและสถานะงานทั้ง 4 การ์ดบนแดชบอร์ด' : 'Monitor KPI summary cards & totals' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  <div class="flow-step">
                    <div class="flow-step-num">02</div>
                    <div class="flow-icon bg-primary text-white"><CIcon name="cil-building" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '2. สถิติ 5 อันดับหอพัก' : '2. Top 5 Dorms' }}</div>
                    <div class="flow-desc">{{ isThai ? 'ดูอันดับหอพักที่มีการแจ้งซ่อมมากที่สุดเพื่อเฝ้าระวัง' : 'Identify dorms with highest repair volume' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  <div class="flow-step">
                    <div class="flow-step-num">03</div>
                    <div class="flow-icon bg-warning text-dark"><CIcon name="cil-warning" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '3. ติดตามงานค้าง/SLA' : '3. Track SLA Delay' }}</div>
                    <div class="flow-desc">{{ isThai ? 'คัดกรองงานที่เกินกำหนดเข้าซ่อม (SLA) และงานรอนักศึกษาประเมิน' : 'Review overdue tickets exceeding SLA' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>
                  <div class="flow-step">
                    <div class="flow-step-num">04</div>
                    <div class="flow-icon bg-success text-white"><CIcon name="cil-file" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '4. ตรวจสอบรายละเอียด' : '4. Check Details' }}</div>
                    <div class="flow-desc">{{ isThai ? 'เปิดดูรายละเอียดใบแจ้งซ่อมและไทม์ไลน์ความคืบหน้า 5 ขั้นตอน' : 'Inspect ticket details and repair timeline' }}</div>
                  </div>
                </div>
              </div>

              <!-- ========================================== -->
              <!-- WORKFLOW 1: ภาพรวมสถิติและตัวกรองข้อมูล (Dashboard KPI & Filtering) -->
              <!-- ========================================== -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 1</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'ภาพรวมสถิติงานซ่อมนักศึกษาและตัวกรองข้อมูล (Dashboard KPI & Filters)' : 'Dashboard KPI & Filters' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [1,2,3,4]" 
                      :key="'chip-sa-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeSaStep === s }"
                      @click="highlightSaStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'คำแนะนำการใช้งานหน้าแดชบอร์ดภาพรวม: การเลือกช่วงเวลาตัวกรอง การอ่านค่าการ์ดตัวชี้วัด 4 สถานะ และการวิเคราะห์หอพักที่แจ้งซ่อมสูงสุด' 
                    : 'Instructions on using the dashboard: selecting time filters, reading the 4 KPI cards, and analyzing top dormitories with repairs.' }}
                </p>

                <!-- Screenshot 1 Block -->
                <div class="annotated-card mb-4 shadow-sm border rounded-lg overflow-hidden bg-white">
                  <div class="annotated-card-body p-0">
                    <div class="screenshot-canvas-wrapper sa-dash position-relative">
                      <img 
                        :src="saImgDashboardOverview" 
                        :alt="isThai ? 'ภาพหน้าจอแดชบอร์ดภาพรวมงานซ่อมนักศึกษา' : 'Dashboard Overview Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(saImgDashboardOverview, isThai ? 'แดชบอร์ดภาพรวมงานซ่อมนักศึกษา' : 'Student Affairs Dashboard Overview')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 495" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-sa1" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-sa1" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-sa1" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 1: ตัวกรองช่วงเวลา สัปดาห์ / เดือน / ปี -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 1 }" @click="highlightSaStep(1)">
                          <path d="M 850 49 L 890 49" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa1)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sa1)">
                            <rect x="630" y="35" width="220" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="645" cy="49" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="645" y="53" class="pin-text">1</text>
                            <text x="663" y="53" class="callout-label">
                              {{ isThai ? 'ตัวกรองช่วงเวลา (ปี/เดือน) *' : 'Time Filter (Year/Month) *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 2: ตรวจสอบสถานะภาพรวม 4 การ์ด -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 2 }" @click="highlightSaStep(2)">
                          <path d="M 120 195 L 120 158" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa1)"/>
                          <g filter="url(#glow-shadow-sa1)">
                            <rect x="35" y="195" width="275" height="28" rx="14" class="callout-pill"/>
                            <circle cx="50" cy="209" r="10" class="pin-badge"/>
                            <text x="50" y="213" class="pin-text">2</text>
                            <text x="68" y="213" class="callout-label">
                              {{ isThai ? 'ตรวจสอบสถานะภาพรวม 4 การ์ด *' : 'Monitor 4 KPI Summary Cards *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 3: อาคารที่แจ้งซ่อมมากที่สุด 5 อันดับ -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 3 }" @click="highlightSaStep(3)">
                          <path d="M 640 190 L 560 215" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa1)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sa1)">
                            <rect x="640" y="172" width="265" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="655" cy="186" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="655" y="190" class="pin-text">3</text>
                            <text x="673" y="190" class="callout-label">
                              {{ isThai ? 'สถิติ 5 อันดับหอพักแจ้งซ่อมสูงสุด *' : 'Top 5 Dorms by Request Volume *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 4: รายการงานที่ควรติดตาม และปุ่ม ดูงานทั้งหมด -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 4 }" @click="highlightSaStep(4)">
                          <path d="M 855 358 L 935 358" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa1)"/>
                          <g filter="url(#glow-shadow-sa1)">
                            <rect x="570" y="344" width="285" height="28" rx="14" class="callout-pill"/>
                            <circle cx="585" cy="358" r="10" class="pin-badge"/>
                            <text x="585" y="362" class="pin-text">4</text>
                            <text x="603" y="362" class="callout-label">
                              {{ isThai ? 'ตรวจสอบงานที่ควรติดตาม / ดูทั้งหมด *' : 'Overdue Follow-up & View All *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step 1, 2, 3, 4 Cards -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-3 mb-2">
                          <div 
                            id="step-card-sa-1"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 1 }"
                            @mouseenter="activeSaStep = 1"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">1</span>
                            <strong class="d-block mb-1">{{ isThai ? 'เลือกตัวกรองช่วงเวลา' : 'Time Period Filter' }}</strong>
                            <small class="text-muted">{{ isThai ? 'เลือกดูสถิติตาม "สัปดาห์", "เดือน" หรือ "ปี" ข้อมูลกราฟจะปรับเปลี่ยนตามตัวกรองทันที' : 'Select week, month, or year to filter dashboard statistics.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div 
                            id="step-card-sa-2"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 2 }"
                            @mouseenter="activeSaStep = 2"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">2</span>
                            <strong class="d-block mb-1">{{ isThai ? 'การ์ดสรุป 4 สถานะ' : '4 KPI Status Cards' }}</strong>
                            <small class="text-muted">{{ isThai ? 'อ่านยอดคำร้องทั้งหมด, รอรับเรื่อง, มอบหมายช่าง และกำลังซ่อม เพื่อดูภาระงานรวม' : 'Track totals for All, Pending, Assigned, and In Progress.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div 
                            id="step-card-sa-3"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 3 }"
                            @mouseenter="activeSaStep = 3"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">3</span>
                            <strong class="d-block mb-1">{{ isThai ? '5 อันดับหอพักแจ้งซ่อม' : 'Top 5 Dormitories' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ตรวจสอบหอพักที่มีปัญหาบ่อยที่สุด เช่น หอพักสักทอง 2 เพื่อประสานงานดูแลเป็นพิเศษ' : 'Identify dorms with the highest repair frequencies.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div 
                            id="step-card-sa-4"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 4 }"
                            @mouseenter="activeSaStep = 4"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">4</span>
                            <strong class="d-block mb-1">{{ isThai ? 'งานที่ควรติดตาม / ดูทั้งหมด' : 'Follow-up & View All' }}</strong>
                            <small class="text-muted">{{ isThai ? 'รายการงานที่เกินกำหนด SLA หรือซ่อมเสร็จรอนักศึกษาประเมิน คลิก "ดูงานทั้งหมด" เพื่อเปิดตารางงาน' : 'Review overdue tickets and click View All to open the full list.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ========================================== -->
              <!-- WORKFLOW 2: การติดตามงานซ่อมนักศึกษาและคัดกรองสถานะ (Ticket Tracking & Status Filter) -->
              <!-- ========================================== -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 2</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การติดตามงานซ่อมนักศึกษาและคัดกรองสถานะ (Ticket Tracking & Status Filter)' : 'Ticket Tracking & Status Filter' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [5,6,7]" 
                      :key="'chip-sa-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeSaStep === s }"
                      @click="highlightSaStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'คำแนะนำการใช้งานหน้าติดตามงานซ่อม: การสลับแท็บคัดกรองสถานะ การตรวจสอบสถานะ SLA และการคลิกปุ่มดูงาน' 
                    : 'Instructions on tracking student tickets: tab filtering, SLA inspection, and viewing ticket details.' }}
                </p>

                <!-- Screenshot 2 Block -->
                <div class="annotated-card mb-4 shadow-sm border rounded-lg overflow-hidden bg-white">
                  <div class="annotated-card-body p-0">
                    <div class="screenshot-canvas-wrapper sa-tracking position-relative">
                      <img 
                        :src="saImgFollowupTickets" 
                        :alt="isThai ? 'ภาพหน้าจอติดตามงานซ่อมนักศึกษา' : 'Ticket Tracking Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(saImgFollowupTickets, isThai ? 'ติดตามงานซ่อมนักศึกษา' : 'Student Ticket Tracking')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 495" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-sa2" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-sa2" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-sa2" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 5: แท็บตัวกรองสถานะงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 5 }" @click="highlightSaStep(5)">
                          <path d="M 535 110 L 450 110" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa2)"/>
                          <g filter="url(#glow-shadow-sa2)">
                            <rect x="540" y="96" width="245" height="28" rx="14" class="callout-pill"/>
                            <circle cx="555" cy="110" r="10" class="pin-badge"/>
                            <text x="555" y="114" class="pin-text">5</text>
                            <text x="573" y="114" class="callout-label">
                              {{ isThai ? 'สลับแท็บคัดกรองสถานะงาน *' : 'Filter Status Tabs *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 6: ตรวจสอบกำหนดซ่อม และการแจ้งเตือนเกิน SLA -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 6 }" @click="highlightSaStep(6)">
                          <path d="M 740 180 L 815 180" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa2)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sa2)">
                            <rect x="470" y="166" width="270" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="485" cy="180" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="485" y="184" class="pin-text">6</text>
                            <text x="503" y="184" class="callout-label">
                              {{ isThai ? 'ตรวจสอบกำหนดซ่อมและเกิน SLA *' : 'Check SLA & Overdue Notice *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 7: ปุ่ม ดูงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 7 }" @click="highlightSaStep(7)">
                          <path d="M 990 142 L 990 168" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa2)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sa2)">
                            <rect x="775" y="125" width="170" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="790" cy="139" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="790" y="143" class="pin-text">7</text>
                            <text x="808" y="143" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "ดูงาน" *' : 'Click "View Ticket" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step 5, 6, 7 Cards -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-4 mb-2">
                          <div 
                            id="step-card-sa-5"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 5 }"
                            @mouseenter="activeSaStep = 5"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">5</span>
                            <strong class="d-block mb-1">{{ isThai ? 'สลับแท็บคัดกรองสถานะ' : 'Filter by Status Tabs' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกเลือกแท็บ "กำลังดำเนินการ", "ควรติดตาม" (เกิน SLA), หรือ "เสร็จแล้ว" เพื่อคัดกรองงานทันที' : 'Switch between In Progress, Needs Follow-up, or Completed.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div 
                            id="step-card-sa-6"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 6 }"
                            @mouseenter="activeSaStep = 6"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">6</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ตรวจสอบ SLA และงานล่าช้า' : 'Check SLA Deadlines' }}</strong>
                            <small class="text-muted">{{ isThai ? 'สังเกตข้อความสีแดง "เกินกำหนดซ่อม (SLA)" เพื่อเร่งรัดติดตามงานซ่อมที่ล่าช้ากับหน่วยงาน' : 'Identify red overdue flags exceeding SLA deadlines.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div 
                            id="step-card-sa-7"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 7 }"
                            @mouseenter="activeSaStep = 7"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">7</span>
                            <strong class="d-block mb-1">{{ isThai ? 'คลิกปุ่ม "ดูงาน"' : 'Open Ticket Details' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่มสีน้ำเงิน "ดูงาน" ท้ายแถว เพื่อเข้าสู่หน้าตรวจสอบความคืบหน้าอย่างละเอียด' : 'Click the button to inspect the full repair timeline.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ========================================== -->
              <!-- WORKFLOW 3: การตรวจสอบรายละเอียดใบงานและไทม์ไลน์ความคืบหน้า (Ticket Detail & Progress Timeline) -->
              <!-- ========================================== -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 3</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การตรวจสอบรายละเอียดใบงานและไทม์ไลน์ความคืบหน้า (Ticket Detail & Progress Timeline)' : 'Ticket Detail & Progress Timeline' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [8,9,10]" 
                      :key="'chip-sa-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeSaStep === s }"
                      @click="highlightSaStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'คำแนะนำการตรวจสอบข้อมูลใบงาน: ไทม์ไลน์ความคืบหน้า 5 ขั้นตอน ข้อมูลช่างและหน่วยงานผู้รับผิดชอบ และปุ่มย้อนกลับ' 
                    : 'Inspect individual ticket timeline, assigned technician information, and back navigation.' }}
                </p>

                <!-- Screenshot 3 Block -->
                <div class="annotated-card mb-4 shadow-sm border rounded-lg overflow-hidden bg-white">
                  <div class="annotated-card-body p-0">
                    <div class="screenshot-canvas-wrapper sa-detail position-relative">
                      <img 
                        :src="saImgTicketDetailView" 
                        :alt="isThai ? 'ภาพหน้าจอรายละเอียดใบงาน' : 'Ticket Detail Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(saImgTicketDetailView, isThai ? 'รายละเอียดใบงาน (ดูได้อย่างเดียว)' : 'Ticket Detail View (Read-Only)')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 488" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-sa3" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-sa3" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-sa3" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 10: ปุ่ม กลับหน้ารายการ -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 10 }" @click="highlightSaStep(10)">
                          <path d="M 290 70 L 276 70" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa3)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sa3)">
                            <rect x="295" y="56" width="200" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="310" cy="70" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="310" y="74" class="pin-text">10</text>
                            <text x="328" y="74" class="callout-label">
                              {{ isThai ? 'ปุ่ม "กลับหน้ารายการ" *' : 'Back to List Button *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 8: ไทม์ไลน์ความคืบหน้า 5 ขั้นตอน -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 8 }" @click="highlightSaStep(8)">
                          <path d="M 300 160 L 335 160" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa3)"/>
                          <g filter="url(#glow-shadow-sa3)">
                            <rect x="40" y="146" width="255" height="28" rx="14" class="callout-pill"/>
                            <circle cx="55" cy="160" r="10" class="pin-badge"/>
                            <text x="55" y="164" class="pin-text">8</text>
                            <text x="73" y="164" class="callout-label">
                              {{ isThai ? 'ติดตามความคืบหน้า 5 ขั้นตอน *' : '5-Step Repair Progress Bar *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 9: ตรวจสอบข้อมูลช่างผู้รับผิดชอบ -->
                        <g class="annotation-group" :class="{ 'is-active': activeSaStep === 9 }" @click="highlightSaStep(9)">
                          <path d="M 330 310 L 330 268" class="pointer-arrow" marker-end="url(#arrowhead-blue-sa3)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-sa3)">
                            <rect x="230" y="310" width="310" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="245" cy="324" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="245" y="328" class="pin-text">9</text>
                            <text x="263" y="328" class="callout-label">
                              {{ isThai ? 'ตรวจสอบข้อมูลผู้แจ้งและหน่วยงาน *' : 'Check Reporter & Department Info *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step 8, 9, 10 Cards -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-4 mb-2">
                          <div 
                            id="step-card-sa-8"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 8 }"
                            @mouseenter="activeSaStep = 8"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">8</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ไทม์ไลน์ความคืบหน้า 5 ขั้นตอน' : '5-Step Repair Timeline' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ติดตามสถานะตั้งแต่: แจ้งเรื่อง -> มอบหมายช่าง -> กำลังซ่อม -> ซ่อมเสร็จ รอประเมิน -> ประเมินแล้ว' : 'Track from submission, assignment, in progress, pending rating, to rated.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div 
                            id="step-card-sa-9"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 9 }"
                            @mouseenter="activeSaStep = 9"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">9</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ข้อมูลช่างและหน่วยงานผู้ซ่อม' : 'Technician & Department Info' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ตรวจดูชื่อช่างผู้รับผิดชอบ (เช่น ชัย บัวคำ), หน่วยงาน และกำหนดเวลาเข้าซ่อมโดยประมาณ' : 'Inspect assigned technician, department, and estimated repair date.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div 
                            id="step-card-sa-10"
                            class="step-item-card h-100"
                            :class="{ active: activeSaStep === 10 }"
                            @mouseenter="activeSaStep = 10"
                            @mouseleave="activeSaStep = null"
                          >
                            <span class="step-num-circle mb-2">10</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ปุ่ม "กลับหน้ารายการ"' : 'Back to Ticket List' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่มสีน้ำเงินมุมบนซ้าย เพื่อย้อนกลับไปดูรายการใบแจ้งซ่อมทั้งหมด' : 'Click back button to return to the ticket tracking list.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </CTab>

<CTab :title="$t('userManual.tabAdmin')" v-if="userRole === 'admin'">
            <div class="manual-admin-container mt-3">

              <!-- Section A: Overview & Supervisor Parity Notice -->
              <div class="workflow-summary-section mb-5">
                <div class="alert alert-primary border-primary d-flex align-items-center p-3 mb-4 rounded-lg shadow-sm" style="background: #eff6ff; border-left: 5px solid #1e40af !important;">
                  <CIcon name="cil-shield-alt" size="xl" class="text-primary mr-3 flex-shrink-0" style="width: 32px; height: 32px;" />
                  <div>
                    <h6 class="font-weight-bold mb-1 text-primary" style="font-size: 1.05rem;">
                      {{ isThai ? 'สิทธิ์และการทำงานของผู้ดูแลระบบ (Admin Role & Supervisor Parity)' : 'Administrator Permissions & Capabilities' }}
                    </h6>
                    <p class="mb-0 text-muted" style="font-size: 0.9rem; line-height: 1.5;">
                      {{ isThai
                        ? 'ผู้ดูแลระบบ (Admin) มีสิทธิ์และความสามารถดำเนินงานเหมือนหัวหน้างาน (Supervisor) ได้ทุกประการ ทั้งการคัดกรองใบแจ้งซ่อม มอบหมายช่าง ติดตามงาน และส่งต่องานข้ามหน่วยงาน พร้อมทั้งมีฟังก์ชันพิเศษเพิ่มเติมเฉพาะ Admin ในการบริหารจัดการฐานข้อมูลทั้งหมดของระบบ (จัดการเจ้าหน้าที่, โครงสร้างส่วนงาน/ฝ่าย, ประเภทงานซ่อม, ข้อมูลอาคาร, และการประเมินผล)'
                        : 'Administrators have full operational parity with Supervisors (triage, assign, track, forward) plus exclusive system management privileges (Staff, Departments, Job Types, Buildings, and Evaluations).' }}
                    </p>
                  </div>
                </div>

                <div class="d-flex align-items-center mb-3">
                  <div class="section-badge mr-2">Overview</div>
                  <h5 class="manual-section-title mb-0">
                    {{ isThai ? 'โครงสร้างการทำงานของผู้ดูแลระบบ (Administrator Architecture)' : 'Administrator Workflow Architecture' }}
                  </h5>
                </div>

                <div class="flowchart-container">
                  <div class="flow-step">
                    <div class="flow-step-num">01</div>
                    <div class="flow-icon bg-primary text-white"><CIcon name="cil-speedometer" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '1. มอบหมาย & ดำเนินงานซ่อม' : '1. Supervisor Operations' }}</div>
                    <div class="flow-desc">{{ isThai ? 'คัดกรองงาน มอบหมายงานช่าง และส่งต่องานข้ามหน่วยงานเหมือน Supervisor ทุกประการ' : 'Full supervisor operational workflow (assign, track, forward, close)' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>

                  <div class="flow-step">
                    <div class="flow-step-num">02</div>
                    <div class="flow-icon bg-info text-white"><CIcon name="cil-people" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '2. จัดการข้อมูลเจ้าหน้าที่' : '2. Staff Management' }}</div>
                    <div class="flow-desc">{{ isThai ? 'เพิ่ม ลบ แก้ไข ข้อมูลช่าง หัวหน้างาน ส่วนพัฒฯ พร้อมนำเข้าผ่านไฟล์ Excel' : 'Manage user accounts, assign roles (Tech/Sup/SA), and batch import via Excel' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>

                  <div class="flow-step">
                    <div class="flow-step-num">03</div>
                    <div class="flow-icon bg-warning text-dark"><CIcon name="cil-sitemap" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '3. โครงสร้างงาน & SLA' : '3. Structure & SLA' }}</div>
                    <div class="flow-desc">{{ isThai ? 'กำหนดส่วนงาน ฝ่าย และประเภทงานแจ้งซ่อม พร้อมตั้งค่าชั่วโมง SLA มาตรฐาน' : 'Configure sections, departments, repair job types, and standard SLA hours' }}</div>
                  </div>
                  <div class="flow-arrow"><CIcon name="cil-arrow-right" size="xl"/></div>

                  <div class="flow-step">
                    <div class="flow-step-num">04</div>
                    <div class="flow-icon bg-success text-white"><CIcon name="cil-building" size="lg"/></div>
                    <div class="flow-title">{{ isThai ? '4. อาคาร & สังกัดผู้แจ้ง' : '4. Buildings & Mapping' }}</div>
                    <div class="flow-desc">{{ isThai ? 'ดูแลข้อมูลอาคารสถานที่ และเชื่อมโยงการจัดส่งใบงานตามสังกัดของผู้แจ้งซ่อม' : 'Manage university buildings and map ticket dispatching by reporter affiliation' }}</div>
                  </div>
                </div>
              </div>

              <!-- ================= WORKFLOW 1: DATA MANAGEMENT MENUS ================= -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 1</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การเข้าถึงเมนูจัดการข้อมูลระบบ (Accessing Data Management Menus)' : 'Accessing Data Management Menus' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [1,2]" 
                      :key="'chip-admin-menu-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeAdminStep === s }"
                      @click="highlightAdminStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'ผู้ดูแลระบบสามารถเข้าถึงเมนูการตั้งค่าและจัดการข้อมูลทั้งหมดได้จากดรอปดาวน์ "จัดการข้อมูล" บนแถบเมนูด้านบนของทุกหน้าจอ' 
                    : 'Administrators can access all master configuration menus via the "Manage Data" dropdown on the top navigation bar.' }}
                </p>

                <!-- Part 1: Top Navigation Dropdown -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper admin-menu">
                      <img 
                        :src="adminImgMenuDropdown" 
                        :alt="isThai ? 'ภาพหน้าจอเมนูจัดการข้อมูล' : 'Data Management Dropdown Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(adminImgMenuDropdown, isThai ? 'เมนูจัดการข้อมูลบนแถบเมนูด้านบน' : 'Data Management Topbar Dropdown')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 365" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin1" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin1" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin1" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 1: คลิกเมนู จัดการข้อมูล (ชี้ตรงไปที่ปุ่มจัดการข้อมูล) -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 1 }" @click="highlightAdminStep(1)">
                          <path d="M 235 32 L 295 32" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin1)"/>
                          <g filter="url(#glow-shadow-admin1)">
                            <rect x="35" y="18" width="200" height="28" rx="14" class="callout-pill"/>
                            <circle cx="50" cy="32" r="10" class="pin-badge"/>
                            <text x="50" y="36" class="pin-text">1</text>
                            <text x="68" y="36" class="callout-label">
                              {{ isThai ? 'คลิกเมนู "จัดการข้อมูล" *' : 'Click "Manage Data" *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 2: เลือกเมนูย่อยที่ต้องการ (ชี้เข้าหากล่องรายการดรอปดาวน์) -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 2 }" @click="highlightAdminStep(2)">
                          <path d="M 525 90 L 410 90" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin1)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin1)">
                            <rect x="525" y="76" width="245" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="540" cy="90" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="540" y="94" class="pin-text">2</text>
                            <text x="558" y="94" class="callout-label">
                              {{ isThai ? 'เลือกเมนูย่อยที่ต้องการจัดการ *' : 'Select Submenu to Manage *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 1 - 2 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-admin-1" :class="{ active: activeAdminStep === 1 }" @mouseenter="activeAdminStep = 1" @mouseleave="activeAdminStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">1</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิกเมนู "จัดการข้อมูล"' : 'Click "Manage Data" Menu' }}</strong>
                              <span class="badge badge-danger ml-auto small font-weight-bold">{{ isThai ? 'จำเป็น' : 'Required' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'บนแถบนำทางส่วนบน (Top Navigation Bar) ให้คลิกที่เมนู "จัดการข้อมูล" เพื่อเปิดแถบรายการการดูแลระบบทั้งหมด' 
                                : 'Click on the "Manage Data" dropdown menu on the top navigation bar to open the master administration options.' }}
                            </p>
                          </div>
                        </div>

                        <div class="col-md-6 mb-3">
                          <div class="step-item-card h-100" id="step-card-admin-2" :class="{ active: activeAdminStep === 2 }" @mouseenter="activeAdminStep = 2" @mouseleave="activeAdminStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">2</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'เลือกเมนูย่อยที่ต้องการจัดการ' : 'Select Target Submenu' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'ดำเนินการ' : 'Action' }}</span>
                            </div>
                            <p class="step-text text-muted mb-2">
                              {{ isThai 
                                ? 'ประกอบด้วย 5 เมนูย่อยหลัก: 1) จัดการข้อมูล เจ้าหน้าที่ 2) จัดการส่วนงานและประเภทงาน 3) จัดการข้อมูล อาคาร 4) เชื่อมข้อมูลสังกัดผู้แจ้งซ่อม และ 5) ประเมินผลช่างเทคนิค' 
                                : 'Features 5 primary management modules: Staff Management, Sections & Job Types, Buildings, Affiliation Mapping, and Technician Evaluation.' }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ================= WORKFLOW 2: STAFF MANAGEMENT ================= -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 2</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การจัดการข้อมูลเจ้าหน้าที่ (Staff Management)' : 'Staff & Technician Management' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [3,4,5,6,7,8,9]" 
                      :key="'chip-admin-staff-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeAdminStep === s }"
                      @click="highlightAdminStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'ขั้นตอนการค้นหาและตรวจสอบรายชื่อเจ้าหน้าที่ในระบบ การแก้ไข/ลบข้อมูล และการเปิดหน้าต่างเพิ่มเจ้าหน้าที่ใหม่รายบุคคลหรือนำเข้าผ่าน Excel' 
                    : 'Guide on searching staff, editing/deleting accounts, and adding single or batch users via Excel import.' }}
                </p>

                <!-- Part 2A: Staff Table List -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper admin-staff">
                      <img 
                        :src="adminImgStaffList" 
                        :alt="isThai ? 'ภาพหน้าจอจัดการข้อมูลเจ้าหน้าที่' : 'Staff List Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(adminImgStaffList, isThai ? 'หน้ารายการจัดการข้อมูล เจ้าหน้าที่' : 'Staff Management Table View')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 476" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin2" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin2" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin2" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 3: ค้นหารายชื่อเจ้าหน้าที่ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 3 }" @click="highlightAdminStep(3)">
                          <path d="M 95 230 L 95 180" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin2)"/>
                          <g filter="url(#glow-shadow-admin2)">
                            <rect x="25" y="235" width="220" height="28" rx="14" class="callout-pill"/>
                            <circle cx="40" cy="249" r="10" class="pin-badge"/>
                            <text x="40" y="253" class="pin-text">3</text>
                            <text x="58" y="253" class="callout-label">
                              {{ isThai ? 'ค้นหารายชื่อเจ้าหน้าที่ *' : 'Search Staff Name *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 4: คลิกปุ่ม + เพิ่มเจ้าหน้าที่ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 4 }" @click="highlightAdminStep(4)">
                          <path d="M 720 74 L 920 74" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin2)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin2)">
                            <rect x="510" y="60" width="215" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="525" cy="74" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="525" y="78" class="pin-text">4</text>
                            <text x="543" y="78" class="callout-label">
                              {{ isThai ? 'คลิก "+ เพิ่มเจ้าหน้าที่" *' : 'Click "+ Add Staff" *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 5: ปุ่ม แก้ไข / ลบ ในแถว -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 5 }" @click="highlightAdminStep(5)">
                          <path d="M 735 215 L 925 215" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin2)"/>
                          <g filter="url(#glow-shadow-admin2)">
                            <rect x="520" y="201" width="220" height="28" rx="14" class="callout-pill"/>
                            <circle cx="535" cy="215" r="10" class="pin-badge"/>
                            <text x="535" y="219" class="pin-text">5</text>
                            <text x="553" y="219" class="callout-label">
                              {{ isThai ? 'ปุ่ม "แก้ไข" / "ลบ" ข้อมูล *' : '"Edit" / "Delete" Actions *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 3 - 5 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-4 mb-3">
                          <div class="step-item-card h-100" id="step-card-admin-3" :class="{ active: activeAdminStep === 3 }" @mouseenter="activeAdminStep = 3" @mouseleave="activeAdminStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">3</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'ค้นหารายชื่อเจ้าหน้าที่' : 'Search Staff' }}</strong>
                            </div>
                            <p class="step-text text-muted mb-0">
                              {{ isThai 
                                ? 'พิมพ์ชื่อ-นามสกุล, อีเมล หรือเลือกแสดงตามหน่วยงานเพื่อค้นหาเจ้าหน้าที่ได้อย่างรวดเร็ว' 
                                : 'Search by name, email, or filter by affiliated department to quickly locate staff members.' }}
                            </p>
                          </div>
                        </div>

                        <div class="col-md-4 mb-3">
                          <div class="step-item-card h-100" id="step-card-admin-4" :class="{ active: activeAdminStep === 4 }" @mouseenter="activeAdminStep = 4" @mouseleave="activeAdminStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">4</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'คลิกปุ่ม "+ เพิ่มเจ้าหน้าที่"' : 'Click "+ Add Staff"' }}</strong>
                              <span class="badge badge-primary ml-auto small font-weight-bold">{{ isThai ? 'เปิดฟอร์ม' : 'Form' }}</span>
                            </div>
                            <p class="step-text text-muted mb-0">
                              {{ isThai 
                                ? 'คลิกปุ่มสีขาวมุมขวาบนของแบนเนอร์ เพื่อเปิดหน้าต่างกรอกข้อมูลเจ้าหน้าที่ใหม่เข้าสู่ระบบ' 
                                : 'Click the button in the upper right header banner to launch the new staff registration modal.' }}
                            </p>
                          </div>
                        </div>

                        <div class="col-md-4 mb-3">
                          <div class="step-item-card h-100" id="step-card-admin-5" :class="{ active: activeAdminStep === 5 }" @mouseenter="activeAdminStep = 5" @mouseleave="activeAdminStep = null">
                            <div class="d-flex align-items-center mb-2">
                              <span class="step-num-circle">5</span>
                              <strong class="step-card-title ml-2">{{ isThai ? 'ปุ่ม "แก้ไข" หรือ "ลบ"' : 'Edit or Delete' }}</strong>
                            </div>
                            <p class="step-text text-muted mb-0">
                              {{ isThai 
                                ? 'แก้ไขเบอร์โทรศัพท์ ฝ่าย หรือประเภทงานช่าง และสามารถลบบัญชีผู้ใช้ที่พ้นสภาพได้ทันที' 
                                : 'Update contact info, department assignment, or delete inactive staff records.' }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Part 2B: Add Staff Modal -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper admin-add-staff">
                      <img 
                        :src="adminImgAddStaffModal" 
                        :alt="isThai ? 'ภาพหน้าต่างเพิ่มเจ้าหน้าที่' : 'Add Staff Modal Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(adminImgAddStaffModal, isThai ? 'หน้าต่างเพิ่มเจ้าหน้าที่' : 'Add Staff Modal View')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 725 855" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin3" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin3" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin3" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 6: กรอกข้อมูลส่วนตัว -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 6 }" @click="highlightAdminStep(6)">
                          <path d="M 360 116 L 360 148" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin3)"/>
                          <g filter="url(#glow-shadow-admin3)">
                            <rect x="200" y="88" width="320" height="28" rx="14" class="callout-pill"/>
                            <circle cx="215" cy="102" r="10" class="pin-badge"/>
                            <text x="215" y="106" class="pin-text">6</text>
                            <text x="233" y="106" class="callout-label">
                              {{ isThai ? 'กรอกชื่อ นามสกุล อีเมล เบอร์โทรศัพท์ *' : 'Enter Name, Email & Phone *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 7: เลือกประเภทผู้ใช้ และฝ่ายรับผิดชอบ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 7 }" @click="highlightAdminStep(7)">
                          <path d="M 335 446 L 335 465" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin3)"/>
                          <g filter="url(#glow-shadow-admin3)">
                            <rect x="160" y="418" width="345" height="28" rx="14" class="callout-pill"/>
                            <circle cx="175" cy="432" r="10" class="pin-badge"/>
                            <text x="175" y="436" class="pin-text">7</text>
                            <text x="193" y="436" class="callout-label">
                              {{ isThai ? 'เลือกประเภทผู้ใช้ (Technician/Supervisor) และฝ่าย *' : 'Select Role & Department *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 8: เพิ่มด้วยไฟล์ .xlsx -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 8 }" @click="highlightAdminStep(8)">
                          <path d="M 314 753 L 314 778" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin3)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin3)">
                            <rect x="180" y="725" width="260" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="195" cy="739" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="195" y="743" class="pin-text">8</text>
                            <text x="213" y="743" class="callout-label">
                              {{ isThai ? 'ปุ่ม "เพิ่มด้วยไฟล์ .xlsx" (นำเข้าชุด)' : '"Import via .xlsx" Button' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 9: คลิกปุ่ม + เพิ่ม -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 9 }" @click="highlightAdminStep(9)">
                          <path d="M 643 753 L 643 778" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin3)"/>
                          <g filter="url(#glow-shadow-admin3)">
                            <rect x="465" y="725" width="215" height="28" rx="14" class="callout-pill"/>
                            <circle cx="480" cy="739" r="10" class="pin-badge"/>
                            <text x="480" y="743" class="pin-text">9</text>
                            <text x="498" y="743" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "+ เพิ่ม" เพื่อบันทึก *' : 'Click "+ Add" to Confirm *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 6 - 9 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-3 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-6" :class="{ active: activeAdminStep === 6 }" @mouseenter="activeAdminStep = 6" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">6</span>
                            <strong class="d-block mb-1">{{ isThai ? 'กรอกข้อมูลส่วนตัว' : 'Personal Info' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ชื่อ, นามสกุล, อีเมลสถาบัน (@mfu.ac.th), และเบอร์โทรศัพท์ที่ติดต่อได้' : 'Name, surname, MFU email, and reachable phone number.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-7" :class="{ active: activeAdminStep === 7 }" @mouseenter="activeAdminStep = 7" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">7</span>
                            <strong class="d-block mb-1">{{ isThai ? 'กำหนดบทบาทและฝ่าย' : 'Role & Department' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ระบุประเภท (Technician, Supervisor, Student Affairs) ฝ่ายที่สังกัด และประเภทงานของช่าง' : 'Select user role, department assignment, and repair specialty.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-8" :class="{ active: activeAdminStep === 8 }" @mouseenter="activeAdminStep = 8" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">8</span>
                            <strong class="d-block mb-1">{{ isThai ? 'นำเข้าด้วย Excel' : 'Batch Excel Import' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ดาวน์โหลดตัวอย่างเทมเพลต และกดเพิ่มด้วยไฟล์ .xlsx เพื่อลงทะเบียนหลายคนพร้อมกัน' : 'Download template and upload .xlsx to register multiple accounts.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-9" :class="{ active: activeAdminStep === 9 }" @mouseenter="activeAdminStep = 9" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">9</span>
                            <strong class="d-block mb-1">{{ isThai ? 'คลิกปุ่ม "+ เพิ่ม"' : 'Click "+ Add"' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ตรวจสอบความถูกต้องและคลิกปุ่มสีน้ำเงินเพื่อบันทึกผู้ใช้เข้าสู่ระบบทันที' : 'Review entries and click button to finalize registration.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ================= WORKFLOW 3: SECTION & JOB TYPES MANAGEMENT ================= -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 3</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การจัดการโครงสร้างส่วนงาน ฝ่าย และประเภทงานแจ้งซ่อม (Sections, Departments & Job Types)' : 'Sections, Departments & Job Types Management' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [10,11,12,13,14,15,16]" 
                      :key="'chip-admin-sec-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeAdminStep === s }"
                      @click="highlightAdminStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'การสร้างและบริหารโครงสร้างลำดับชั้นของระบบซ่อมบำรุง: ส่วนงาน (Section) -> ฝ่าย (Department) -> ประเภทงานแจ้งซ่อม (Job Types) พร้อมตั้งเวลา SLA มาตรฐาน' 
                    : 'Manage structural hierarchy of maintenance organization: Section -> Department -> Job Types, along with SLA hours.' }}
                </p>

                <!-- Part 3A: Structure Hierarchy View -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper admin-section">
                      <img 
                        :src="adminImgSectionDeptJob" 
                        :alt="isThai ? 'ภาพหน้าจอโครงสร้างส่วนงานและประเภทงาน' : 'Section Structure Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(adminImgSectionDeptJob, isThai ? 'หน้าโครงสร้างส่วนงาน ฝ่าย และประเภทงานซ่อม' : 'Section, Department & Job Types Hierarchy View')"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 494" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin4" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin4" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin4" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 10: คลิกปุ่ม + เพิ่มส่วนงานใหม่ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 10 }" @click="highlightAdminStep(10)">
                          <path d="M 925 70 L 955 70" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin4)"/>
                          <g filter="url(#glow-shadow-admin4)">
                            <rect x="690" y="56" width="235" height="28" rx="14" class="callout-pill"/>
                            <circle cx="705" cy="70" r="10" class="pin-badge"/>
                            <text x="705" y="74" class="pin-text">10</text>
                            <text x="723" y="74" class="callout-label">
                              {{ isThai ? 'คลิก "+ เพิ่มส่วนงานใหม่" *' : 'Click "+ Add Section" *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 11: ปุ่ม + เพิ่มฝ่าย / แก้ไขส่วนงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 11 }" @click="highlightAdminStep(11)">
                          <path d="M 865 208 L 882 208" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin4)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin4)">
                            <rect x="615" y="194" width="250" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="630" cy="208" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="630" y="212" class="pin-text">11</text>
                            <text x="648" y="212" class="callout-label">
                              {{ isThai ? 'ปุ่ม "+ เพิ่มฝ่าย" / แก้ไขส่วนงาน' : '"+ Add Dept" / Edit Section' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 12: ปุ่ม + เพิ่มประเภทงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 12 }" @click="highlightAdminStep(12)">
                          <path d="M 880 254 L 902 254" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin4)"/>
                          <g filter="url(#glow-shadow-admin4)">
                            <rect x="640" y="240" width="240" height="28" rx="14" class="callout-pill"/>
                            <circle cx="655" cy="254" r="10" class="pin-badge"/>
                            <text x="655" y="258" class="pin-text">12</text>
                            <text x="673" y="258" class="callout-label">
                              {{ isThai ? 'ปุ่ม "+ เพิ่มประเภทงาน" *' : 'Click "+ Add Job Type" *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 13: กำหนดเวลา SLA และสถานะ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 13 }" @click="highlightAdminStep(13)">
                          <path d="M 815 330 L 842 330" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin4)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin4)">
                            <rect x="470" y="316" width="345" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="485" cy="330" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="485" y="334" class="pin-text">13</text>
                            <text x="503" y="334" class="callout-label">
                              {{ isThai ? 'กำหนดชั่วโมง SLA และสวิตช์เปิด/ปิดสถานะ' : 'Set SLA Hours & Status Toggle' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 10 - 13 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-3 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-10" :class="{ active: activeAdminStep === 10 }" @mouseenter="activeAdminStep = 10" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">10</span>
                            <strong class="d-block mb-1">{{ isThai ? 'เพิ่มส่วนงานใหม่' : 'Add Section' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่มสีขาวมุมขวาบน เพื่อสร้างส่วนงานหลักใหม่ระดับบนสุด' : 'Create a top-level administrative section (e.g., Facility Division).' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-11" :class="{ active: activeAdminStep === 11 }" @mouseenter="activeAdminStep = 11" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">11</span>
                            <strong class="d-block mb-1">{{ isThai ? 'เพิ่มฝ่ายย่อย' : 'Add Department' }}</strong>
                            <small class="text-muted">{{ isThai ? 'เพิ่มฝ่ายรับผิดชอบย่อยใต้ส่วนงานหลัก และสามารถแก้ไขชื่อหรือลบได้' : 'Add specialized departments within the parent section.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-12" :class="{ active: activeAdminStep === 12 }" @mouseenter="activeAdminStep = 12" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">12</span>
                            <strong class="d-block mb-1">{{ isThai ? 'เพิ่มประเภทงาน' : 'Add Job Type' }}</strong>
                            <small class="text-muted">{{ isThai ? 'เพิ่มหมวดหมู่งานซ่อมที่เปิดให้ผู้ใช้งานแจ้งในระบบ เช่น ซ่อมประปา ซ่อมไฟฟ้า' : 'Define user-selectable repair categories.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-3 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-13" :class="{ active: activeAdminStep === 13 }" @mouseenter="activeAdminStep = 13" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">13</span>
                            <strong class="d-block mb-1">{{ isThai ? 'กำหนด SLA และสถานะ' : 'Configure SLA & Status' }}</strong>
                            <small class="text-muted">{{ isThai ? 'กำหนดชั่วโมงแก้ไขตามเกณฑ์ SLA และเปิด/ปิดการแจ้งซ่อมของประเภทงานนั้นได้' : 'Set standard SLA deadline and toggle category availability.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Part 3B: Add Section Modal -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">

                    <div class="screenshot-canvas-wrapper admin-add-section">
                      <img 
                        :src="adminImgAddSectionModal" 
                        :alt="isThai ? 'ภาพหน้าต่างเพิ่มส่วนงานใหม่' : 'Add Section Modal Screenshot'"
                        class="screenshot-base-img" 
                      style="cursor: pointer;"
                        @click="openImageModal(adminImgAddSectionModal, isThai ? 'หน้าต่างเพิ่มส่วนงานใหม่ (Section)' : 'Add Section Modal View')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 431 360" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin5" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin5" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin5" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 14: กรอกรหัสส่วนงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 14 }" @click="highlightAdminStep(14)">
                          <path d="M 215 90 L 215 118" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin5)"/>
                          <g filter="url(#glow-shadow-admin5)">
                            <rect x="75" y="62" width="280" height="28" rx="14" class="callout-pill"/>
                            <circle cx="90" cy="76" r="10" class="pin-badge"/>
                            <text x="90" y="80" class="pin-text">14</text>
                            <text x="108" y="80" class="callout-label">
                              {{ isThai ? 'กรอกรหัสส่วนงาน (Section Code) *' : 'Enter Section Code *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 15: กรอกชื่อส่วนงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 15 }" @click="highlightAdminStep(15)">
                          <path d="M 305 182 L 305 198" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin5)"/>
                          <g filter="url(#glow-shadow-admin5)">
                            <rect x="215" y="154" width="180" height="28" rx="14" class="callout-pill"/>
                            <circle cx="230" cy="168" r="10" class="pin-badge"/>
                            <text x="230" y="172" class="pin-text">15</text>
                            <text x="248" y="172" class="callout-label">
                              {{ isThai ? 'กรอกชื่อส่วนงาน *' : 'Enter Section Name *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 16: คลิกปุ่ม เพิ่มส่วนงาน -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 16 }" @click="highlightAdminStep(16)">
                          <path d="M 358 262 L 358 278" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin5)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin5)">
                            <rect x="195" y="234" width="200" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="210" cy="248" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="210" y="252" class="pin-text">16</text>
                            <text x="228" y="252" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "เพิ่มส่วนงาน" *' : 'Click "Add Section" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 14 - 16 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-14" :class="{ active: activeAdminStep === 14 }" @mouseenter="activeAdminStep = 14" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">14</span>
                            <strong class="d-block mb-1">{{ isThai ? 'รหัสส่วนงาน' : 'Section Code' }}</strong>
                            <small class="text-muted">{{ isThai ? 'กรอกรหัสตัวย่อภาษาอังกฤษ (เช่น IT, MAINT, HR) สำหรับใช้อ้างอิงในระบบ' : 'Short code used for internal taxonomy reference.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-15" :class="{ active: activeAdminStep === 15 }" @mouseenter="activeAdminStep = 15" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">15</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ชื่อส่วนงาน' : 'Section Name' }}</strong>
                            <small class="text-muted">{{ isThai ? 'กรอกชื่อส่วนงานเต็มภาษาไทย เช่น "ส่วนอาคารสถานที่", "ศูนย์เทคโนโลยีฯ"' : 'Full official Thai name of the section.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-16" :class="{ active: activeAdminStep === 16 }" @mouseenter="activeAdminStep = 16" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">16</span>
                            <strong class="d-block mb-1">{{ isThai ? 'คลิก "เพิ่มส่วนงาน"' : 'Confirm Add' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่มสีน้ำเงินเพื่อบันทึกและสร้างส่วนงานลงสู่ฐานข้อมูล' : 'Click the button to store the new section record.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ================= WORKFLOW 4: BUILDING & ROOM MANAGEMENT ================= -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 4</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การจัดการข้อมูลอาคารและห้อง (Building & Room Management)' : 'Building & Room Management' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [17,18,19,20,21]" 
                      :key="'chip-admin-bld-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeAdminStep === s }"
                      @click="highlightAdminStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'การตรวจสอบรายชื่ออาคารและห้องพัก/ห้องเรียนในระบบ พร้อมการแก้ไขชื่ออาคาร 2 ภาษา (ไทย-อังกฤษ) และการจัดการห้องที่เปิดให้บริการ' 
                    : 'Manage university buildings and rooms taxonomy, edit bilingual names, and configure active room records.' }}
                </p>

                <!-- Part 4A: Building Table List -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper admin-building">
                      <img 
                        :src="adminImgBuildingManagement" 
                        :alt="isThai ? 'ภาพหน้าจอรายการจัดการข้อมูลอาคาร' : 'Building Management Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(adminImgBuildingManagement, isThai ? 'หน้ารายการจัดการข้อมูล อาคาร และห้อง' : 'Building & Room Table View')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 495" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin6" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin6" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin6" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 17: ตรวจสอบรายชื่ออาคารและห้องพัก -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 17 }" @click="highlightAdminStep(17)">
                          <path d="M 160 353 L 160 375" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin6)"/>
                          <g filter="url(#glow-shadow-admin6)">
                            <rect x="40" y="325" width="280" height="28" rx="14" class="callout-pill"/>
                            <circle cx="55" cy="339" r="10" class="pin-badge"/>
                            <text x="55" y="343" class="pin-text">17</text>
                            <text x="73" y="343" class="callout-label">
                              {{ isThai ? 'ตรวจสอบรายชื่ออาคาร และห้องพัก *' : 'Building & Rooms List *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 18: ปุ่มแก้ไขและลบอาคาร -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 18 }" @click="highlightAdminStep(18)">
                          <path d="M 870 353 L 925 380" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin6)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin6)">
                            <rect x="660" y="325" width="240" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="675" cy="339" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="675" y="343" class="pin-text">18</text>
                            <text x="693" y="343" class="callout-label">
                              {{ isThai ? 'ปุ่ม "แก้ไข" หรือ "ลบ" ข้อมูล *' : 'Edit / Delete Actions *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 17 - 18 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-17" :class="{ active: activeAdminStep === 17 }" @mouseenter="activeAdminStep = 17" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">17</span>
                            <strong class="d-block mb-1">{{ isThai ? 'รายการอาคารและห้อง' : 'Building & Room Table' }}</strong>
                            <small class="text-muted">{{ isThai ? 'เรียกดูรายชื่ออาคารและรายการห้องที่สังกัด ตรวจสอบความถูกต้องของข้อมูลสถานที่ในระบบ' : 'View buildings, associated room numbers, and current system records.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-6 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-18" :class="{ active: activeAdminStep === 18 }" @mouseenter="activeAdminStep = 18" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">18</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ปุ่มแก้ไข / ลบ' : 'Edit / Delete Actions' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่มสีฟ้า "แก้ไข" เพื่อเปิดหน้าต่างปรับปรุงข้อมูลอาคารและห้อง หรือปุ่มสีแดง "ลบ" เพื่อลบอาคาร' : 'Click "Edit" to modify building details or "Delete" to remove building.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Part 4B: Edit Building Modal -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card" style="max-width: 520px; margin: 0 auto;">
                    <div class="screenshot-canvas-wrapper admin-edit-building">
                      <img 
                        :src="adminImgEditBuildingModal" 
                        :alt="isThai ? 'ภาพหน้าต่างแก้ไขข้อมูลอาคาร' : 'Edit Building Modal Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(adminImgEditBuildingModal, isThai ? 'หน้าต่างแก้ไขข้อมูลอาคารและห้อง' : 'Edit Building & Rooms Modal View')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 505 904" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin7" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin7" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin7" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 21: ปุ่ม ปิด หน้าต่าง -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 21 }" @click="highlightAdminStep(21)">
                          <path d="M 360 62 L 398 62" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin7)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin7)">
                            <rect x="150" y="48" width="200" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="165" cy="62" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="165" y="66" class="pin-text">21</text>
                            <text x="183" y="66" class="callout-label">
                              {{ isThai ? 'ปุ่ม "ปิด" หน้าต่าง *' : '"Close" Modal Button *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 19: แก้ไขชื่ออาคารภาษาไทยและอังกฤษ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 19 }" @click="highlightAdminStep(19)">
                          <path d="M 252 213 L 252 238" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin7)"/>
                          <g filter="url(#glow-shadow-admin7)">
                            <rect x="75" y="185" width="350" height="28" rx="14" class="callout-pill"/>
                            <circle cx="90" cy="199" r="10" class="pin-badge"/>
                            <text x="90" y="203" class="pin-text">19</text>
                            <text x="108" y="203" class="callout-label">
                              {{ isThai ? 'แก้ไขชื่ออาคารภาษาไทยและอังกฤษ *' : 'Edit Thai & English Names *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 20: จัดการรายการห้องย่อย -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 20 }" @click="highlightAdminStep(20)">
                          <path d="M 252 416 L 252 435" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin7)"/>
                          <g filter="url(#glow-shadow-admin7)">
                            <rect x="105" y="388" width="295" height="28" rx="14" class="callout-pill"/>
                            <circle cx="120" cy="402" r="10" class="pin-badge"/>
                            <text x="120" y="406" class="pin-text">20</text>
                            <text x="138" y="406" class="callout-label">
                              {{ isThai ? 'ติ๊กเลือก/จัดการรายชื่อห้องพัก *' : 'Manage Room Checkboxes *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 19 - 21 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-19" :class="{ active: activeAdminStep === 19 }" @mouseenter="activeAdminStep = 19" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">19</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ชื่ออาคาร 2 ภาษา' : 'Bilingual Names' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ระบุชื่ออาคารภาษาไทยและภาษาอังกฤษเพื่อรองรับการใช้งานหลายภาษาในระบบ' : 'Update official Thai and English building names.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-20" :class="{ active: activeAdminStep === 20 }" @mouseenter="activeAdminStep = 20" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">20</span>
                            <strong class="d-block mb-1">{{ isThai ? 'จัดการรายการห้อง' : 'Room Checkboxes' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ตรวจสอบรายชื่อห้อง และเลือกติ๊กเปิด/ปิดห้องที่เปิดให้แจ้งซ่อมได้' : 'Toggle room availability and edit room naming.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-21" :class="{ active: activeAdminStep === 21 }" @mouseenter="activeAdminStep = 21" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">21</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ปิดหน้าต่าง' : 'Close Modal' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่ม "ปิด" ด้านบนเพื่อเสร็จสิ้นการตั้งค่าข้อมูลอาคาร' : 'Close the edit modal window.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ================= WORKFLOW 5: ORGANIZATION DATA SYNC (BLENDATA / MASTER DATA) ================= -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 5</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การเชื่อมข้อมูลสังกัดผู้แจ้งซ่อม (Master Data & Blendata Sync)' : 'Organization Master Data Sync' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [22,23,24,25,26,27]" 
                      :key="'chip-admin-sync-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeAdminStep === s }"
                      @click="highlightAdminStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>

                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'การเชื่อมโยง นำเข้า และตั้งค่ารอบซิงค์ข้อมูลสังกัดผู้แจ้งซ่อมจาก University Master Data / Blendata เข้าสู่ระบบแจ้งซ่อมแบบอัตโนมัติ' 
                    : 'Monitor integration, run manual sync, and configure automated background synchronization schedule.' }}
                </p>

                <!-- Part 5A: Connection & Sync Status -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper admin-sync-status">
                      <img 
                        :src="adminImgSyncOrgStatus" 
                        :alt="isThai ? 'ภาพหน้าจอสถานะการซิงค์ข้อมูล' : 'Sync Status Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(adminImgSyncOrgStatus, isThai ? 'สถานะการเชื่อมต่อ Blendata และ Master Data' : 'Integration & Sync Status View')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 926 428" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin8" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin8" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin8" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 22: ตรวจสอบสถานะ Blendata และ Master Data -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 22 }" @click="highlightAdminStep(22)">
                          <path d="M 245 243 L 245 285" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin8)"/>
                          <g filter="url(#glow-shadow-admin8)">
                            <rect x="60" y="215" width="370" height="28" rx="14" class="callout-pill"/>
                            <circle cx="75" cy="229" r="10" class="pin-badge"/>
                            <text x="75" y="233" class="pin-text">22</text>
                            <text x="93" y="233" class="callout-label">
                              {{ isThai ? 'ตรวจสอบสถานะ Blendata และ Master Data *' : 'Blendata & Master Data Status *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 23: สถานะ Auto Sync และ Worker -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 23 }" @click="highlightAdminStep(23)">
                          <path d="M 670 243 L 670 285" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin8)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin8)">
                            <rect x="490" y="215" width="360" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="505" cy="229" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="505" y="233" class="pin-text">23</text>
                            <text x="523" y="233" class="callout-label">
                              {{ isThai ? 'ตรวจสอบสถานะ Auto Sync และรอบถัดไป *' : 'Auto Sync Status & Schedule *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 22 - 23 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-22" :class="{ active: activeAdminStep === 22 }" @mouseenter="activeAdminStep = 22" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">22</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ตรวจสอบสถานะเชื่อมต่อ' : 'Integration Status' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ดูสถานะการเชื่อมต่อระหว่าง Blendata และฐานข้อมูลกลาง University Master Data' : 'Verify active connection to external university data sources.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-6 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-23" :class="{ active: activeAdminStep === 23 }" @mouseenter="activeAdminStep = 23" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">23</span>
                            <strong class="d-block mb-1">{{ isThai ? 'สถานะรอบซิงค์' : 'Sync Schedule & Worker' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ตรวจสอบรอบเวลาซิงค์ถัดไป และสถานะการทำงานของ Background Worker' : 'Check next automated sync schedule and worker status.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Part 5B: Sync Actions & History -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper admin-sync-actions">
                      <img 
                        :src="adminImgSyncOrgActions" 
                        :alt="isThai ? 'ภาพหน้าจอการสั่งซิงค์ข้อมูล' : 'Sync Actions Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(adminImgSyncOrgActions, isThai ? 'การสั่งซิงค์ข้อมูลและดูประวัติ' : 'Manual Sync & History View')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 968 610" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin9" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin9" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin9" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 24: ปุ่ม ซิงค์ข้อมูลสังกัดผู้แจ้งซ่อมตอนนี้ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 24 }" @click="highlightAdminStep(24)">
                          <path d="M 442 56 L 442 80" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin9)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin9)">
                            <rect x="240" y="26" width="404" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="255" cy="40" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="255" y="44" class="pin-text">24</text>
                            <text x="273" y="44" class="callout-label">
                              {{ isThai ? 'คลิก "ซิงค์ข้อมูลสังกัดผู้แจ้งซ่อมตอนนี้" (Sync Data Now) *' : 'Click "Sync Requester Affiliation Data Now" *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 25: ตรวจสอบรายการอัปเดตและประวัติ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 25 }" @click="highlightAdminStep(25)">
                          <path d="M 370 236 L 332 236" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin9)"/>
                          <g filter="url(#glow-shadow-admin9)">
                            <rect x="375" y="222" width="330" height="28" rx="14" class="callout-pill"/>
                            <circle cx="390" cy="236" r="10" class="pin-badge"/>
                            <text x="390" y="240" class="pin-text">25</text>
                            <text x="408" y="240" class="callout-label">
                              {{ isThai ? 'คลิก Update Organization หรือดู History *' : 'Update Org or View History *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 24 - 25 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-24" :class="{ active: activeAdminStep === 24 }" @mouseenter="activeAdminStep = 24" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">24</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ซิงค์ข้อมูลสังกัดทันที' : 'Sync Affiliation Data Now' }}</strong>
                            <small class="text-muted">{{ isThai ? 'กดปุ่มเพื่อตรวจสอบและซิงค์ข้อมูลสังกัดล่าสุด หากพบความเปลี่ยนแปลงจะมีตารางพรีวิวให้ยืนยันก่อนบันทึกจริง' : 'Click button to verify and sync affiliations. A preview modal prompts for confirmation before saving.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-6 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-25" :class="{ active: activeAdminStep === 25 }" @mouseenter="activeAdminStep = 25" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">25</span>
                            <strong class="d-block mb-1">{{ isThai ? 'บันทึกสังกัดและดูประวัติ' : 'Update Org & History' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิก Update Organization เพื่อบันทึกสังกัดใหม่ลงฐานข้อมูล และตรวจสอบประวัติการซิงค์' : 'Save organization structure changes and view audit history.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Part 5C: Auto Sync & Schedule Configuration -->
                <div class="screenshot-guide-block mb-4">
                  <div class="annotated-card">
                    <div class="screenshot-canvas-wrapper admin-sync-schedule">
                      <img 
                        :src="adminImgSyncOrgSchedule" 
                        :alt="isThai ? 'ภาพหน้าจอการตั้งค่ารอบซิงค์อัตโนมัติ' : 'Sync Schedule Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(adminImgSyncOrgSchedule, isThai ? 'การตั้งค่ารอบซิงค์อัตโนมัติ (Auto Sync Settings)' : 'Automated Sync Configuration')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 961 684" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin10" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin10" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin10" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 26: ตั้งค่าความถี่และเวลาของ Auto Sync -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 26 }" @click="highlightAdminStep(26)">
                          <path d="M 200 228 L 200 268" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin10)"/>
                          <g filter="url(#glow-shadow-admin10)">
                            <rect x="40" y="200" width="340" height="28" rx="14" class="callout-pill"/>
                            <circle cx="55" cy="214" r="10" class="pin-badge"/>
                            <text x="55" y="218" class="pin-text">26</text>
                            <text x="73" y="218" class="callout-label">
                              {{ isThai ? 'เลือกความถี่ (วัน/เดือน/ปี) และเวลาซิงค์ *' : 'Frequency (Day/Month/Year) & Time *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 27: คลิกปุ่ม บันทึกการตั้งค่า -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 27 }" @click="highlightAdminStep(27)">
                          <path d="M 780 540 L 850 575" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin10)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin10)">
                            <rect x="630" y="515" width="230" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="645" cy="529" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="645" y="533" class="pin-text">27</text>
                            <text x="663" y="533" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "บันทึกการตั้งค่า" *' : 'Click "Save Settings" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 26 - 27 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-6 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-26" :class="{ active: activeAdminStep === 26 }" @mouseenter="activeAdminStep = 26" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">26</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ตั้งค่าความถี่รอบซิงค์' : 'Auto Sync Schedule' }}</strong>
                            <small class="text-muted">{{ isThai ? 'เปิดสวิตช์ Auto Apply กำหนดความถี่ เช่น ทุก 1 วัน และระบุเวลาที่ต้องการให้ระบบซิงค์อัตโนมัติ' : 'Set periodic frequency and daily trigger time for automated sync.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-6 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-27" :class="{ active: activeAdminStep === 27 }" @mouseenter="activeAdminStep = 27" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">27</span>
                            <strong class="d-block mb-1">{{ isThai ? 'บันทึกการตั้งค่า' : 'Save Configuration' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่มสีน้ำเงิน "บันทึกการตั้งค่า" เพื่อนำตารางเวลาไปใช้ในระบบ Background Worker' : 'Save configuration parameters to background worker scheduler.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>



              <!-- ================= WORKFLOW 6: TECHNICIAN PERFORMANCE EVALUATION ================= -->
              <div class="step-guide-section mb-5">
                <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                  <div class="d-flex align-items-center">
                    <div class="section-badge bg-primary text-white mr-2">Workflow 6</div>
                    <h5 class="manual-section-title mb-0">
                      {{ isThai ? 'การประเมินผลการปฏิบัติงานของช่างเทคนิค (Technician Evaluation)' : 'Technician Performance Evaluation' }}
                    </h5>
                  </div>
                  <div class="step-quick-nav d-none d-md-flex align-items-center gap-1 mt-2 mt-md-0">
                    <span class="text-muted small mr-1 font-weight-bold">{{ isThai ? 'ขั้นตอน:' : 'Steps:' }}</span>
                    <button 
                      v-for="s in [28,29,30,31,32,33]" 
                      :key="'chip-admin-'+s"
                      type="button"
                      class="btn btn-sm step-chip"
                      :class="{ active: activeAdminStep === s }"
                      @click="highlightAdminStep(s)"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>
                <p class="text-muted small mb-4">
                  {{ isThai 
                    ? 'ระบบติดตามและประเมินผลงานช่างเทคนิครายบุคคล การกรองตามแผนก การส่งออกรายงานผลงานเป็นไฟล์ Excel ตลอดจนการตรวจสอบสถิติงานซ่อม ความตรงต่อเวลาตาม SLA และคะแนนความพึงพอใจรายใบงาน' 
                    : 'Monitor individual technician performance, filter by department, export performance reports to Excel, and inspect SLA compliance and user rating details.' }}
                </p>

                <!-- Part 6A: ตารางสรุปผลงานช่างและการส่งออก Excel (ขั้นตอน 28 - 30) -->
                <div class="annotated-card mb-4 shadow-sm border rounded-lg overflow-hidden bg-white">
                  <div class="annotated-card-body p-0">
                    <div class="screenshot-canvas-wrapper admin-eval-list position-relative">
                      <img 
                        :src="adminImgEvalTechList" 
                        :alt="isThai ? 'ภาพหน้าจอตารางประเมินผลช่างเทคนิค' : 'Technician Evaluation List Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(adminImgEvalTechList, isThai ? 'ตารางประเมินผลช่างเทคนิคและส่งออก Excel' : 'Technician Evaluation List & Excel Export')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1024 483" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin11" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin11" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin11" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 28: ตัวกรองตามแผนก -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 28 }" @click="highlightAdminStep(28)">
                          <path d="M 680 105 L 720 83" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin11)"/>
                          <g filter="url(#glow-shadow-admin11)">
                            <rect x="470" y="105" width="250" height="28" rx="14" class="callout-pill"/>
                            <circle cx="485" cy="119" r="10" class="pin-badge"/>
                            <text x="485" y="123" class="pin-text">28</text>
                            <text x="503" y="123" class="callout-label">
                              {{ isThai ? 'เลือกตัวกรองแผนก *' : 'Filter by Department *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 29: ปุ่ม ดาวน์โหลด Excel -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 29 }" @click="highlightAdminStep(29)">
                          <path d="M 910 105 L 930 83" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin11)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin11)">
                            <rect x="730" y="105" width="280" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="745" cy="119" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="745" y="123" class="pin-text">29</text>
                            <text x="763" y="123" class="callout-label">
                              {{ isThai ? 'ปุ่ม "ดาวน์โหลด Excel" *' : 'Download Excel *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 30: ปุ่ม รายละเอียด ท้ายแถว -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 30 }" @click="highlightAdminStep(30)">
                          <path d="M 940 440 L 970 415" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin11)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin11)">
                            <rect x="650" y="440" width="300" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="665" cy="454" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="665" y="458" class="pin-text">30</text>
                            <text x="683" y="458" class="callout-label">
                              {{ isThai ? 'คลิกปุ่ม "รายละเอียด" *' : 'Click "Details" *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 28 - 30 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-28" :class="{ active: activeAdminStep === 28 }" @mouseenter="activeAdminStep = 28" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">28</span>
                            <strong class="d-block mb-1">{{ isThai ? 'กรองข้อมูลตามแผนก' : 'Department Filter' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิก Dropdown "ทุกแผนก" เพื่อกรองดูผลการประเมินเฉพาะส่วนงานหรือฝ่ายที่ต้องการ' : 'Select a specific department or view all departments.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-29" :class="{ active: activeAdminStep === 29 }" @mouseenter="activeAdminStep = 29" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">29</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ส่งออกไฟล์ Excel' : 'Export Excel' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่มสีเขียว "ดาวน์โหลด Excel" เพื่อนำสถิติผลงานช่างทั้งหมดไปสรุปรายงาน' : 'Export technician performance metrics to an Excel workbook.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-30" :class="{ active: activeAdminStep === 30 }" @mouseenter="activeAdminStep = 30" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">30</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ดูรายละเอียดงานรายช่าง' : 'View Technician Details' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ตรวจดูสถิติรับงาน ปิดงาน % SLA ดาวเฉลี่ย แล้วคลิกปุ่มสีน้ำเงิน "รายละเอียด" เพื่อดูงานย่อย' : 'Review KPI metrics and click Details to inspect individual tickets.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Part 6B: หน้าต่างรายละเอียดผลงานรายช่าง (ขั้นตอน 31 - 33) -->
                <div class="annotated-card mb-4 shadow-sm border rounded-lg overflow-hidden bg-white">
                  <div class="annotated-card-body p-0">
                    <div class="screenshot-canvas-wrapper admin-eval-modal position-relative">
                      <img 
                        :src="adminImgEvalTechModal" 
                        :alt="isThai ? 'ภาพหน้าต่างรายละเอียดผลงานของช่าง' : 'Technician Job Details Modal Screenshot'"
                        class="screenshot-base-img" 
                        style="cursor: pointer;"
                        @click="openImageModal(adminImgEvalTechModal, isThai ? 'หน้าต่างรายละเอียดผลงานรายช่าง' : 'Technician Performance Details Modal')"
                        :title="isThai ? 'คลิกเพื่อขยายดูภาพเต็ม' : 'Click to zoom fullscreen'"
                      />
                      <svg class="annotation-svg-overlay" viewBox="0 0 1000 444" preserveAspectRatio="none">
                        <defs>
                          <filter id="glow-shadow-admin12" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.45"/>
                          </filter>
                          <marker id="arrowhead-red-admin12" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                          <marker id="arrowhead-blue-admin12" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#2563eb"/>
                          </marker>
                        </defs>

                        <!-- STEP 33: ปุ่มปิดหน้าต่าง Modal -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 33 }" @click="highlightAdminStep(33)">
                          <path d="M 830 77 L 860 77" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin12)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin12)">
                            <rect x="630" y="63" width="195" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="645" cy="77" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="645" y="81" class="pin-text">33</text>
                            <text x="663" y="81" class="callout-label">
                              {{ isThai ? 'ปุ่ม "ปิด" หน้าต่าง' : 'Close Modal' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 31: ตรวจสอบรายการใบแจ้งซ่อมและสถานะ -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 31 }" @click="highlightAdminStep(31)">
                          <path d="M 370 380 L 485 315" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin12)"/>
                          <g filter="url(#glow-shadow-admin12)">
                            <rect x="70" y="380" width="310" height="28" rx="14" class="callout-pill"/>
                            <circle cx="85" cy="394" r="10" class="pin-badge"/>
                            <text x="85" y="398" class="pin-text">31</text>
                            <text x="103" y="398" class="callout-label">
                              {{ isThai ? 'ตรวจสอบสถานะใบแจ้งซ่อม *' : 'Check Ticket Status *' }}
                            </text>
                          </g>
                        </g>

                        <!-- STEP 32: ตรวจสอบ SLA และผลคะแนนประเมินดาว -->
                        <g class="annotation-group" :class="{ 'is-active': activeAdminStep === 32 }" @click="highlightAdminStep(32)">
                          <path d="M 680 380 L 680 325" class="pointer-arrow" marker-end="url(#arrowhead-blue-admin12)" style="stroke: #2563eb;"/>
                          <g filter="url(#glow-shadow-admin12)">
                            <rect x="490" y="380" width="330" height="28" rx="14" class="callout-pill" style="fill: #1e3a8a;"/>
                            <circle cx="505" cy="394" r="10" class="pin-badge" style="fill: #2563eb;"/>
                            <text x="505" y="398" class="pin-text">32</text>
                            <text x="523" y="398" class="callout-label">
                              {{ isThai ? 'ตรวจสอบ SLA และคะแนนดาว *' : 'Check SLA & Star Rating *' }}
                            </text>
                          </g>
                        </g>
                      </svg>
                    </div>

                    <!-- Step Cards 31 - 33 -->
                    <div class="steps-detail-grid p-3">
                      <div class="row">
                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-31" :class="{ active: activeAdminStep === 31 }" @mouseenter="activeAdminStep = 31" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">31</span>
                            <strong class="d-block mb-1">{{ isThai ? 'รายการใบแจ้งซ่อมและสถานะ' : 'Ticket History & Status' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ดูรายการใบแจ้งซ่อมที่ช่างได้รับมอบหมาย เช่น "เสร็จสิ้น (ประเมินแล้ว)" หรือ "มอบหมายช่าง"' : 'Inspect ticket IDs, report dates, job types, and execution status.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-32" :class="{ active: activeAdminStep === 32 }" @mouseenter="activeAdminStep = 32" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">32</span>
                            <strong class="d-block mb-1">{{ isThai ? 'การตรงต่อเวลาและคะแนนดาว' : 'SLA & Customer Rating' }}</strong>
                            <small class="text-muted">{{ isThai ? 'ตรวจสอบความตรงเวลาตาม SLA (ทันเวลา / เกินกำหนด) พร้อมคะแนนดาว 1 - 5 ดาว และข้อเสนอแนะ' : 'Verify SLA adherence (On time vs Overdue), star rating (e.g. 5/5), and feedback comments.' }}</small>
                          </div>
                        </div>

                        <div class="col-md-4 mb-2">
                          <div class="step-item-card h-100" id="step-card-admin-33" :class="{ active: activeAdminStep === 33 }" @mouseenter="activeAdminStep = 33" @mouseleave="activeAdminStep = null">
                            <span class="step-num-circle mb-2">33</span>
                            <strong class="d-block mb-1">{{ isThai ? 'ปิดหน้าต่างรายละเอียด' : 'Close Modal' }}</strong>
                            <small class="text-muted">{{ isThai ? 'คลิกปุ่ม "ปิด" หรือเครื่องหมายกากบาทมุมบนขวา เพื่อกลับสู่หน้ารายการหลัก' : 'Click the close button to dismiss the modal and return to the list.' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </CTab>
        </CTabs>

      </CCardBody>
    </CCard>
    <!-- Custom Fullscreen Lightbox Modal -->
    <div v-if="modalVisible" class="custom-lightbox-overlay" @click.self="modalVisible = false">
      <div class="custom-lightbox-content">
        <div class="custom-lightbox-header">
          <h6 class="mb-0 text-white font-weight-bold d-flex align-items-center">
            <CIcon name="cil-image" class="mr-2"/> {{ modalTitle }}
          </h6>
          <button type="button" class="btn btn-sm btn-light py-0 px-2 font-weight-bold" @click="modalVisible = false">
            ✕ {{ isThai ? 'ปิด' : 'Close' }}
          </button>
        </div>
        <div class="custom-lightbox-body">
          <img :src="modalImg" :alt="modalTitle" class="custom-lightbox-img" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  imgTop, 
  imgBottom, 
  supImgDashboard, 
  supImgListForward, 
  supImgAssignDropdown, 
  supImgAssignConfirm, 
  supImgForwardDept,
  techImgDashboard,
  techImgAcceptJob,
  techImgConfirmAccept,
  techImgUpdateEta,
  techImgCompleteJob,
  saImgDashboardOverview,
  saImgFollowupTickets,
  saImgTicketDetailView,
  adminImgMenuDropdown,
  adminImgStaffList,
  adminImgAddStaffModal,
  adminImgSectionDeptJob,
  adminImgAddSectionModal,
  adminImgBuildingManagement,
  adminImgEditBuildingModal,
  adminImgSyncOrgStatus,
  adminImgSyncOrgActions,
  adminImgSyncOrgSchedule,
  adminImgEvalTechList,
  adminImgEvalTechModal
} from "@/assets/manual/manualImages";

export default {
  name: "UserManual",
  data() {
    return {
      userRole: "user",
      activeStep: null,
      activeSupStep: null,
      activeSupFwdStep: null,
      activeTechStep: null,
      activeSaStep: null,
      activeAdminStep: null,
      modalVisible: false,
      modalImg: "",
      modalTitle: "",
      imgTop,
      imgBottom,
      supImgDashboard,
      supImgListForward,
      supImgAssignDropdown,
      supImgAssignConfirm,
      supImgForwardDept,
      techImgDashboard,
      techImgAcceptJob,
      techImgConfirmAccept,
      techImgUpdateEta,
      techImgCompleteJob,
      saImgDashboardOverview,
      saImgFollowupTickets,
      saImgTicketDetailView,
      adminImgMenuDropdown,
      adminImgStaffList,
      adminImgAddStaffModal,
      adminImgSectionDeptJob,
      adminImgAddSectionModal,
      adminImgBuildingManagement,
      adminImgEditBuildingModal,
      adminImgSyncOrgStatus,
      adminImgSyncOrgActions,
      adminImgSyncOrgSchedule,
      adminImgEvalTechList,
      adminImgEvalTechModal
    };
  },
  computed: {
    isThai() {
      const lang = this.$store?.state?.locale?.lang || 'th';
      return lang === 'th';
    }
  },
  methods: {
    scrollToStep(targetId) {
      this.$nextTick(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.remove('step-jump-pulse');
          void el.offsetWidth;
          el.classList.add('step-jump-pulse');
          setTimeout(() => {
            if (el) el.classList.remove('step-jump-pulse');
          }, 1800);
        }
      });
    },
    highlightStep(num) {
      this.activeStep = num;
      this.scrollToStep('step-card-user-' + num);
    },
    highlightSupStep(num) {
      this.activeSupStep = num;
      this.scrollToStep('step-card-sup-' + num);
    },
    highlightSupFwdStep(num) {
      this.activeSupFwdStep = num;
      this.scrollToStep('step-card-sup-fwd-' + num);
    },
    highlightTechStep(num) {
      this.activeTechStep = num;
      this.scrollToStep('step-card-tech-' + num);
    },
    highlightSaStep(num) {
      this.activeSaStep = num;
      this.scrollToStep('step-card-sa-' + num);
    },
    highlightAdminStep(num) {
      this.activeAdminStep = num;
      this.scrollToStep('step-card-admin-' + num);
    },
    openImageModal(src, title) {
      this.modalImg = src;
      this.modalTitle = title;
      this.modalVisible = true;
    }
  },
  mounted() {
    const userStored = localStorage.getItem("user");
    if (userStored) {
      try {
        const parsed = JSON.parse(userStored);
        if (parsed && parsed.role) {
          this.userRole = parsed.role;
        }
      } catch (e) {
        console.error("Error parsing user role", e);
      }
    }
  }
};
</script>

<style scoped>
.manual-page { width: 100%; max-width: none; margin: 0; padding-bottom: 40px; }
.manual-card { overflow:hidden; border:1px solid rgba(37, 99, 235,.12); border-radius:0 0 12px 12px !important; box-shadow:0 4px 16px rgba(0, 0, 0, 0.04) !important; }
.manual-card > .manual-header:first-child,
.manual-header.user-page-banner {
  display:flex !important;
  align-items:center !important;
  width:100% !important;
  min-height:90px !important;
  margin:0 !important;
  padding:20px 28px !important;
  border:0 !important;
  border-radius:0 !important;
  color:#fff;
  background:#1e40af;
  box-shadow:none !important;
}
.manual-header .card-title { display:flex; align-items:center; font-weight:800; font-size: 1.25rem; }
.manual-section-title { color:#1e40af; font-size:1.15rem; font-weight:800; }
.manual-body { background:#ffffff; }

.section-badge {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Tabs styling */
.manual-body ::v-deep .nav-pills { gap: 8px; margin-bottom: 12px; }
.manual-body ::v-deep .nav-pills .nav-link {
  color: #64748b !important;
  border-radius: 10px !important;
  font-weight: 700 !important;
  padding: 8px 18px !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  transition: all 0.2s ease !important;
}
.manual-body ::v-deep .nav-pills .nav-link.active {
  color: #ffffff !important;
  background: #1e40af !important;
  border-color: #1e40af !important;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.25) !important;
}
.manual-user-only ::v-deep .nav { display:none; }
.manual-user-only ::v-deep .tab-content { border:0; }

/* Flowchart Overview */
.flowchart-container {
  display: grid;
  grid-template-columns: minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr);
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 24px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
}

.flow-step {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  padding: 24px 16px 20px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s, box-shadow 0.2s;
  min-height: 215px;
}

.flow-step:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 18px rgba(30, 64, 175, 0.1);
  border-color: #bfdbfe;
}

.flow-step-num {
  position: absolute;
  top: 10px;
  right: 14px;
  font-size: 0.75rem;
  font-weight: 800;
  color: #94a3b8;
  letter-spacing: 1px;
}

.flow-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
}

.flow-title {
  font-weight: 800;
  font-size: 0.98rem;
  margin-bottom: 8px;
  color: #1e40af;
}

.flow-desc {
  font-size: 0.84rem;
  color: #64748b;
  line-height: 1.5;
}

.flow-arrow {
  color: #94a3b8;
  display: flex;
  align-items: center;
}

/* Step-by-Step Guide Cards */
.step-cards-list {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.guide-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.guide-card:hover {
  border-color: #bfdbfe;
  box-shadow: 0 8px 24px rgba(30, 64, 175, 0.08);
}

.guide-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.guide-step-pill {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #1e40af;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  font-weight: 900;
  box-shadow: 0 3px 8px rgba(30, 64, 175, 0.25);
  flex-shrink: 0;
}

.guide-step-title {
  color: #0f172a;
  font-weight: 800;
  font-size: 1.05rem;
}

.guide-step-subtitle {
  color: #64748b;
  font-size: 0.85rem;
}

.guide-card-body {
  padding: 24px;
}

/* UI Mockup Window Preview */
/* Annotated Screenshot Styles */
@keyframes stepPulseEffect {
  0% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.6);
    transform: scale(1.02);
  }
  50% {
    box-shadow: 0 0 0 12px rgba(37, 99, 235, 0);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
    transform: scale(1);
  }
}

.step-item-card {
  scroll-margin-top: 100px;
  scroll-margin-bottom: 80px;
  transition: all 0.25s ease-in-out;
}

.step-item-card.step-jump-pulse {
  animation: stepPulseEffect 0.7s cubic-bezier(0.25, 1, 0.5, 1) 2 !important;
  border-color: #2563eb !important;
  background-color: #eff6ff !important;
}

.step-chip {
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 800;
  font-size: 0.8rem;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
  transition: all 0.2s ease;
}
.step-chip:hover, .step-chip.active {
  background: #2563eb;
  color: #ffffff;
  border-color: #2563eb;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
  transform: translateY(-1px);
}

.annotated-card {
  max-width: 1024px;
  margin: 0 auto;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.annotated-card-header {
  background: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.window-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
}
.window-dot.red { background: #ef4444; }
.window-dot.yellow { background: #f59e0b; }
.window-dot.green { background: #10b981; }

.window-title {
  font-size: 0.88rem;
  font-weight: 800;
  color: #1e293b;
}

.zoom-btn {
  font-size: 0.78rem;
  font-weight: 700;
  border-radius: 6px;
}

.screenshot-canvas-wrapper {
  position: relative;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
  line-height: 0;
}

.screenshot-canvas-wrapper.part1 {
  aspect-ratio: 1024 / 442;
  min-height: 240px;
}

.screenshot-canvas-wrapper.part2 {
  aspect-ratio: 1024 / 490;
  min-height: 240px;
}

.screenshot-canvas-wrapper.sup-dash {
  aspect-ratio: 1024 / 470;
  min-height: 240px;
}

.screenshot-canvas-wrapper.sup-list {
  aspect-ratio: 1024 / 462;
  min-height: 240px;
}

.screenshot-canvas-wrapper.sup-dropdown {
  aspect-ratio: 1024 / 644;
  min-height: 280px;
}

.screenshot-canvas-wrapper.sup-confirm {
  aspect-ratio: 1024 / 646;
  min-height: 280px;
}


.screenshot-canvas-wrapper.tech-dash {
  aspect-ratio: 1024 / 480;
  min-height: 240px;
}

.screenshot-canvas-wrapper.tech-accept {
  aspect-ratio: 1024 / 678;
  min-height: 280px;
}

.screenshot-canvas-wrapper.tech-confirm {
  aspect-ratio: 1024 / 667;
  min-height: 280px;
}

.screenshot-canvas-wrapper.tech-eta {
  aspect-ratio: 1024 / 671;
  min-height: 280px;
}

.screenshot-canvas-wrapper.tech-complete {
  aspect-ratio: 1024 / 678;
  min-height: 280px;
}


.screenshot-canvas-wrapper.sa-dash {
  aspect-ratio: 1024 / 495;
  min-height: 200px;
}

.screenshot-canvas-wrapper.sa-followup {
  aspect-ratio: 1024 / 495;
  min-height: 240px;
}

.screenshot-canvas-wrapper.sa-detail {
  aspect-ratio: 1024 / 488;
  min-height: 240px;
}

.screenshot-canvas-wrapper.sup-forward {
  aspect-ratio: 1024 / 652;
  min-height: 280px;
}

.screenshot-canvas-wrapper.admin-menu {
  aspect-ratio: 1024 / 365;
  min-height: 200px;
}

.screenshot-canvas-wrapper.admin-staff {
  aspect-ratio: 1024 / 476;
  min-height: 240px;
}

.screenshot-canvas-wrapper.admin-add-staff {
  aspect-ratio: 725 / 855;
  min-height: 320px;
  max-width: 760px;
  margin: 0 auto;
}

.screenshot-canvas-wrapper.admin-section {
  aspect-ratio: 1024 / 494;
  min-height: 240px;
}

.screenshot-canvas-wrapper.admin-add-section {
  aspect-ratio: 431 / 360;
  min-height: 220px;
  max-width: 540px;
  margin: 0 auto;
}

.screenshot-canvas-wrapper.admin-building {
  aspect-ratio: 1024 / 499;
  min-height: 240px;
}

.screenshot-canvas-wrapper.admin-edit-building {
  aspect-ratio: 505 / 904;
  min-height: 320px;
  max-width: 520px;
  margin: 0 auto;
}

.screenshot-canvas-wrapper.admin-sync-status {
  aspect-ratio: 926 / 428;
  min-height: 220px;
}

.screenshot-canvas-wrapper.admin-sync-actions {
  aspect-ratio: 968 / 610;
  min-height: 260px;
}

.screenshot-canvas-wrapper.admin-sync-schedule {
  aspect-ratio: 961 / 684;
  min-height: 280px;
}

.screenshot-canvas-wrapper.admin-eval-list {
  aspect-ratio: 1024 / 483;
  min-height: 240px;
}

.screenshot-canvas-wrapper.admin-eval-modal {
  aspect-ratio: 1000 / 444;
  min-height: 240px;
  max-width: 900px;
  margin: 0 auto;
}

.screenshot-base-img {
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
  pointer-events: none;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

.annotation-svg-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto;
}

.annotation-group {
  cursor: pointer;
  transition: all 0.25s ease;
}



.pointer-arrow {
  stroke: #2563eb;
  stroke-width: 3.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: all 0.2s ease;
}

.callout-pill {
  fill: #1e3a8a;
  transition: all 0.2s ease;
}

.pin-badge {
  fill: #2563eb;
  transition: all 0.2s ease;
}

.pin-text {
  fill: #ffffff !important;
  font-size: 13px;
  font-weight: 900;
  text-anchor: middle;
  font-family: system-ui, -apple-system, sans-serif;
}

.callout-label {
  fill: #ffffff !important;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}

.annotation-group:hover .pointer-arrow,
.annotation-group.is-active .pointer-arrow {
  stroke: #3b82f6;
  stroke-width: 4.5;
}

.annotation-group:hover .callout-pill,
.annotation-group.is-active .callout-pill {
  fill: #1d4ed8;
}

.annotation-group:hover .pin-badge,
.annotation-group.is-active .pin-badge {
  fill: #3b82f6;
}

.steps-detail-grid {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.step-item-card {
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;
}

.step-item-card:hover, .step-item-card.active {
  border-color: #2563eb;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.16);
  transform: translateY(-2px);
}

.step-num-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #2563eb;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 0.85rem;
  box-shadow: 0 2px 6px rgba(225, 29, 72, 0.3);
}

.step-card-title {
  color: #0f172a;
  font-size: 0.95rem;
}

.step-text {
  font-size: 0.85rem;
  line-height: 1.55;
}

.step-tip-box {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 6px 10px;
  color: #1e3a8a;
  display: flex;
  align-items: flex-start;
}

.status-explain-row {
  display: flex;
  align-items: center;
}

.rating-highlight-box {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
}

/* Lightbox Modal */
.custom-lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.custom-lightbox-content {
  background: #1e293b;
  border-radius: 12px;
  overflow: hidden;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.custom-lightbox-header {
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #334155;
}

.custom-lightbox-body {
  padding: 12px;
  overflow: auto;
  text-align: center;
}

.custom-lightbox-img {
  max-width: 100%;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.ui-mockup-frame {
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

.ui-mockup-header {
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ui-mockup-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.ui-mockup-dot.red { background: #ef4444; }
.ui-mockup-dot.yellow { background: #f59e0b; }
.ui-mockup-dot.green { background: #10b981; }

.ui-mockup-title {
  margin-left: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.3px;
}

.ui-mockup-content {
  padding: 20px;
  background: #fafafa;
}

.mock-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 8px;
}

.mock-category-box {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
}

.mock-section-head {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 10px;
}

.mock-icon-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.mock-icon-tile {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.mock-icon-tile.active {
  background: #eff6ff;
  border-color: #1e40af;
  color: #1e40af;
  box-shadow: 0 0 0 2px #bfdbfe;
}

.mock-tile-tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #0f172a;
  color: #ffffff;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.mock-selected-bar {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.85rem;
}

.mock-textarea {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 12px;
  font-size: 0.88rem;
  color: #1e293b;
  min-height: 64px;
}

.mock-upload-box {
  background: #ffffff;
  border: 2px dashed #93c5fd;
  border-radius: 10px;
  padding: 24px;
  text-align: center;
}

.mock-upload-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.mock-location-toggle {
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.88rem;
  color: #475569;
}

.mock-location-toggle.active {
  background: #eff6ff;
  border-color: #1e40af;
  color: #1e40af;
}

.mock-input-with-icon {
  position: relative;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 14px 10px 38px;
}

.mock-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.mock-input-text {
  font-size: 0.88rem;
}

.mock-input-box {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.9rem;
}

.mock-ticket-item {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
}

.mock-stars {
  font-size: 1.1rem;
  letter-spacing: 2px;
}

/* Instructions List */
.instruction-list {
  padding-left: 20px;
  margin-bottom: 0;
}

.instruction-list li {
  font-size: 0.88rem;
  color: #334155;
  line-height: 1.6;
  margin-bottom: 8px;
}

.instruction-list li:last-child {
  margin-bottom: 0;
}

/* Tips Cards */
.tip-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .flowchart-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .flow-arrow {
    display: none;
  }
  .flow-step {
    min-height: 180px;
  }
}

@media (max-width: 768px) {
  .manual-card > .manual-header:first-child,
  .manual-header.user-page-banner {
    padding: 16px 20px !important;
  }
  .flowchart-container {
    display: flex;
    flex-direction: column;
    padding: 14px;
  }
  .flow-step {
    min-height: auto;
    padding: 18px 14px;
  }
  .guide-card-header {
    padding: 14px 18px;
  }
  .guide-card-body {
    padding: 16px;
  }
  .ui-mockup-content {
    padding: 14px;
  }
}
</style>
